<template>
    <!-- Main list layer. Bulk selection page = phase 3. -->
  <div class="ilm" :class="{ 'ilm--selecting': selectionMode }" dir="rtl">
    <div class="ilm__header">
      <div class="ilm__bar">
        <div class="ilm__bar-actions">
          <button
            v-if="
              $route.name !== 'sellTaxPayer' &&
              $store.state.permissions &&
              $store.state.permissions.includes(invoiceList.type)
            "
            type="button"
            class="ilm__bar-btn ilm__bar-btn--create"
            :aria-label="$t('create')"
            @click="invoiceList.goTo(invoiceList.type)"
          >
            <v-icon size="22" color="#5B7C99">mdi-plus</v-icon>
          </button>
          <button
            type="button"
            class="ilm__bar-btn"
            :aria-label="$t('refresh')"
            @click="onRefreshTap"
          >
            <v-icon size="20" color="#37474F">mdi-refresh</v-icon>
          </button>
        </div>

        <h1 class="ilm__title">{{ $t($route.name) }}</h1>

        <button
          type="button"
          class="ilm__bar-btn ilm__bar-btn--back"
          :aria-label="$t('back')"
          @click="goBack"
        >
          <v-icon size="22" color="#37474F">mdi-arrow-left</v-icon>
        </button>
      </div>

      <div class="ilm__search-row">
        <div class="ilm__search">
          <v-icon size="20" color="#90A4AE" class="ilm__search-icon">
            mdi-magnify
          </v-icon>
          <input
            v-model="invoiceList.search"
            type="search"
            class="ilm__search-input"
            :placeholder="$t('search')"
            autocomplete="off"
          />
          <button
            v-if="invoiceList.search"
            type="button"
            class="ilm__search-clear"
            :aria-label="$t('clear')"
            @click="invoiceList.search = ''"
          >
            <v-icon size="16" color="#90A4AE">mdi-close</v-icon>
          </button>
        </div>

        <!-- Opens the mobile filter sheet on the same shell filter state -->
        <button
          type="button"
          class="ilm__filter-btn"
          :aria-label="$t('filter')"
          @click="onFilterTap"
        >
          <v-icon size="22" color="#546E7A">mdi-filter-outline</v-icon>
        </button>

        <!-- Bulk selection toggle: active state mirrors the cashier-mode
             toggle in the invoice form header -->
        <button
          type="button"
          class="ilm__filter-btn"
          :class="{ 'ilm__filter-btn--active': selectionMode }"
          :aria-label="$t('select')"
          @click="toggleSelectionMode"
        >
          <v-icon size="22" :color="selectionMode ? '#1565C0' : '#546E7A'">
            mdi-checkbox-multiple-marked-outline
          </v-icon>
        </button>
      </div>

      <!-- Selection-mode toolbar: select all loaded / clear + counter -->
      <div v-if="selectionMode" class="ilm__select-bar" dir="rtl">
        <button
          type="button"
          class="ilm__select-bar-btn"
          @click="selectAllLoaded"
        >
          <v-icon size="17" color="#5B7C99">mdi-check-all</v-icon>
          {{ $t("selectAllItems") }}
        </button>
        <span class="ilm__select-count">
          {{ selectedItems.length }} {{ $t("selectedCount") }}
        </span>
        <button
          type="button"
          class="ilm__select-bar-btn"
          @click="clearSelection"
        >
          <v-icon size="17" color="#90A4AE">mdi-close</v-icon>
          {{ $t("deselectAll") }}
        </button>
      </div>
    </div>

    <div v-if="invoiceList.loading && !displayItems.length" class="ilm__state">
      <v-progress-circular indeterminate color="#5B7C99" size="28" />
    </div>

    <div v-else-if="!displayItems.length" class="ilm__state ilm__state--empty">
      <img
        class="ilm__empty-logo"
        src="@/assets/dashboard/no-data-cloud.png"
        alt=""
      />
      <p class="ilm__empty-text">{{ $t("no-data") }}</p>
    </div>

    <div v-else class="ilm__list">
      <article
        v-for="(item, index) in displayItems"
        :key="item.id || index"
        class="ilm-card"
        :class="{ 'ilm-card--selected': selectionMode && isSelected(item) }"
      >
        <!-- Selection-mode tap layer: whole card toggles; selected cards
             get the dark blur layer + check badge -->
        <div
          v-if="selectionMode"
          class="ilm-card__select-overlay"
          :class="{
            'ilm-card__select-overlay--on': isSelected(item),
          }"
          @click="toggleSelect(item)"
        >
          <span v-if="isSelected(item)" class="ilm-card__check">
            <v-icon size="17" color="#FFFFFF">mdi-check</v-icon>
          </span>
        </div>
        <div class="ilm-card__head">
          <span class="ilm-card__customer-wrap">
            <!-- Same customer cell action as desktop: name opens the
                 account (person) component -->
            <button
              v-if="item.customer"
              type="button"
              class="ilm-card__customer ilm-card__customer--link"
              @click="invoiceList.openComponent('account', item.customer_id)"
            >
              <span class="ilm-card__customer-name">
                {{ item.customer.name }}
              </span>
              <v-icon size="12" color="#879EB6">mdi-pencil-outline</v-icon>
            </button>
            <span v-else class="ilm-card__customer">{{ "—" }}</span>
          </span>
          <span class="ilm-card__number">#{{ item.number }}</span>
        </div>

        <div class="ilm-card__meta">
          <span>{{ $t("date") }}: {{ item.date || "—" }}</span>
          <v-icon v-if="item.lock" size="15" color="#EF5350" class="ms-1">
            mdi-lock-outline
          </v-icon>
        </div>

        <div class="ilm-card__amounts">
          <div class="ilm-card__amount-row">
            <span class="ilm-card__amount-label">{{ $t("finalAmount") }}</span>
            <span class="ilm-card__amount-value">
              {{ formatAmount(item, item.price ? item.price.price : 0) }}
            </span>
          </div>
          <div class="ilm-card__amount-row">
            <span class="ilm-card__amount-label">{{ $t("payedPrice") }}</span>
            <span class="ilm-card__amount-value">
              {{
                item.payed
                  ? formatAmount(item, item.payed.amount || 0)
                  : "—"
              }}
            </span>
          </div>
          <div class="ilm-card__amount-row">
            <span class="ilm-card__amount-label">{{ $t("remainPrice") }}</span>
            <span
              class="ilm-card__amount-value"
              :class="
                isRemain(item)
                  ? 'ilm-card__amount-value--remain'
                  : 'ilm-card__amount-value--paid'
              "
            >
              {{ remainText(item) }}
            </span>
          </div>
        </div>

        <div class="ilm-card__footer">
          <div class="ilm-card__actions">
          <!-- Mobile delete confirmation sheet below; same shell delete flow -->
          <button
            v-show="
              removePermission(invoiceList.type) &&
              invoiceList.resolveDeleteBtnVisibility(item)
            "
            type="button"
            class="ilm-btn ilm-btn--danger"
            :disabled="item.lock ? true : false"
            @click="askDelete(item, 'delete')"
          >
            <v-icon size="14" color="#E53935">mdi-trash-can-outline</v-icon>
            {{ $t("remove") }}
          </button>
          <button
            type="button"
            class="ilm-btn ilm-btn--primary"
            :disabled="item.lock ? true : false"
            @click="invoiceList.edit(item)"
          >
            <v-icon size="14" color="#FFFFFF">mdi-pencil-outline</v-icon>
            {{ $t("edit") }}
          </button>
            <button
              type="button"
              class="ilm-btn ilm-btn--outline"
              @click="openDetails(item)"
            >
              <v-icon size="14" color="#5B7C99">mdi-eye-outline</v-icon>
              {{ $t("details") }}
            </button>
          </div>

          <button
            type="button"
            class="ilm-pay-btn"
            :disabled="!hasPaymentDetails(item)"
            @click="openPayedSheet(item)"
          >
            <v-icon
              size="14"
              :color="hasPaymentDetails(item) ? '#5B7C99' : '#90A4AE'"
            >
              mdi-cash-multiple
            </v-icon>
            {{ $t("details") }} {{ $t("payments") }}
          </button>
        </div>
      </article>

      <div v-if="appending" class="ilm__state ilm__state--more">
        <v-progress-circular indeterminate color="#5B7C99" size="22" />
      </div>
    </div>

    <!-- Details sheet: all invoice fields + secondary actions (same shell methods as desktop) -->
    <v-bottom-sheet v-model="detailSheet" scrollable>
      <v-card class="ilm-sheet">
        <div class="ilm-sheet__handle" />
        <div class="ilm-sheet__header" dir="rtl">
          <div class="ilm-sheet__header-side">
            <span v-if="detailItem" class="ilm-sheet__num-chip">
              #{{ detailItem.number }}
            </span>
          </div>
          <div class="ilm-sheet__title">{{ $t("details") }}</div>
          <div class="ilm-sheet__header-side ilm-sheet__header-side--end">
            <button
              type="button"
              class="ilm-sheet__back"
              :aria-label="$t('back')"
              @click="detailSheet = false"
            >
              <v-icon size="22" color="#37474F">mdi-arrow-left</v-icon>
            </button>
          </div>
        </div>

        <!-- Quick actions: fixed under the header; the rest stays in the
             scrollable list below (same runNamedAction + conditions) -->
        <div v-if="detailItem" class="ilm-sheet__quickbar" dir="rtl">
          <button
            type="button"
            class="ilm-sheet__quick"
            @click="runNamedAction('view')"
          >
            <v-icon size="20" color="#5B7C99">mdi-eye-outline</v-icon>
            {{ $t("invoiceViewOnly") }}
          </button>
          <button
            type="button"
            class="ilm-sheet__quick"
            @click="runNamedAction('print')"
          >
            <v-icon size="20" color="#5B7C99">mdi-printer-outline</v-icon>
            {{ $t("print") }}
          </button>
          <button
            v-if="lockPermission"
            type="button"
            class="ilm-sheet__quick"
            @click="runNamedAction('lock')"
          >
            <v-icon size="20" :color="detailItem.lock ? 'green' : 'pink'">
              {{
                detailItem.lock ? "mdi-lock-open-outline" : "mdi-lock-outline"
              }}
            </v-icon>
            {{ detailItem.lock ? $t("unlock") : $t("lock") }}
          </button>
          <button
            v-if="invoiceList.type == 'sell' || invoiceList.type == 'presale'"
            type="button"
            class="ilm-sheet__quick"
            @click="runNamedAction('invoiceSms')"
          >
            <v-icon size="20" color="#5B7C99">mdi-message-text-outline</v-icon>
            {{ $t("sendInvoiceSMS") }}
          </button>
          <button
            v-if="
              invoiceList.taxPayerSystem &&
              (invoiceList.type == 'sell' || invoiceList.type == 'returnSell')
            "
            type="button"
            class="ilm-sheet__quick"
            @click="runNamedAction('taxSend')"
          >
            <v-icon size="20" color="#5B7C99">mdi-cash</v-icon>
            {{ $t("sendToTaxPayerShort") }}
          </button>
          <button
            type="button"
            class="ilm-sheet__quick"
            @click="moreSheet = true"
          >
            <v-icon size="20" color="#5B7C99">mdi-dots-horizontal</v-icon>
            {{ $t("more") }}
          </button>
        </div>

        <!-- Customer part kept apart from the table and fixed under the
             quick bar: flat tinted block with the customer name and the
             turnover navigation row; only the table below scrolls -->
        <div v-if="detailItem" class="ilm-sheet__customer-block" dir="rtl">
          <div class="ilm-sheet__customer-row">
            <span class="ilm-sheet__row-label">{{ $t("detailsName") }}</span>
            <span class="ilm-sheet__customer-name">
              {{
                detailItem.customer ? detailItem.customer.name : "—"
              }}
            </span>
          </div>
          <button
            v-if="detailItem.customer"
            type="button"
            class="ilm-sheet__linkrow"
            @click="
              invoiceList.openComponent(
                'accountingTurnover',
                detailItem.customer.fin_id
              )
            "
          >
            <v-icon size="18" color="#00796B">mdi-playlist-check</v-icon>
            <span class="ilm-sheet__linkrow-text">{{ $t("turnover") }}</span>
            <v-icon size="18" color="#80CBC4">mdi-chevron-left</v-icon>
          </button>
        </div>

        <div v-if="detailItem" class="ilm-sheet__body" dir="rtl">
          <div class="ilm-sheet__card">
            <div
              v-for="(row, i) in detailRows(detailItem).filter(
                (r) => r.kind !== 'customer'
              )"
              :key="i"
              class="ilm-sheet__row"
              :class="{
                'ilm-sheet__row--band':
                  row.kind === 'money' ||
                  row.kind === 'final' ||
                  row.kind === 'remainDue' ||
                  row.kind === 'remainPaid',
              }"
            >
              <span class="ilm-sheet__row-label">{{ row.label }}</span>
              <span
                class="ilm-sheet__row-value"
                :class="{
                  'ilm-sheet__row-value--money':
                    row.kind === 'money' ||
                    row.kind === 'remainDue' ||
                    row.kind === 'remainPaid',
                  'ilm-sheet__row-value--final': row.kind === 'final',
                  'ilm-sheet__row-value--due': row.kind === 'remainDue',
                  'ilm-sheet__row-value--paid': row.kind === 'remainPaid',
                }"
              >
                {{ row.value }}
              </span>
            </div>

            <div
              v-if="integratorDetailRows(detailItem).length"
              class="ilm-sheet__integrator"
            >
              <div
                v-for="(row, idx) in integratorDetailRows(detailItem)"
                :key="'int-' + idx"
                class="ilm-sheet__integrator-block"
              >
                <div class="ilm-sheet__row ilm-sheet__row--id">
                  <span class="ilm-sheet__row-label ilm-sheet__id-label">
                    <img
                      v-if="row.img"
                      :src="row.img"
                      class="ilm-sheet__id-icon"
                      alt=""
                    />
                    {{ row.label }}
                  </span>
                  <button
                    v-if="row.copy"
                    type="button"
                    class="ilm-sheet__row-value ilm-sheet__id-value"
                    v-clipboard:copy="row.copy"
                    @click="onCopyIntegratorId"
                  >
                    {{ row.value }}
                  </button>
                  <span
                    v-else
                    class="ilm-sheet__row-value ilm-sheet__id-value"
                  >
                    {{ row.value }}
                  </span>
                </div>
                <button
                  v-if="row.channel"
                  type="button"
                  class="ilm-sheet__linkrow"
                  @click="openIntegratorOrder(row)"
                >
                  <span class="ilm-sheet__linkrow-text">
                    {{ $t("integratorOrderDetails") }}
                    {{ channelLabel(row.channel) }}
                  </span>
                  <v-icon size="18" color="#80CBC4">mdi-chevron-left</v-icon>
                </button>
              </div>
            </div>

            <div
              v-if="
                invoiceList.isUserHasTaxPayer &&
                (invoiceList.type == 'sell' ||
                  invoiceList.type == 'returnSell')
              "
              class="ilm-sheet__row"
            >
              <span class="ilm-sheet__row-label">{{ $t("system-status") }}</span>
              <span class="ilm-sheet__row-value">
                <v-chip
                  v-if="detailItem.tax_payer"
                  small
                  :color="systemStatusColor(detailItem)"
                  :class="{ 'ilm-sheet__status-chip--click': systemStatusClickable(detailItem) }"
                  @click="onSystemStatusClick"
                >
                  {{ systemStatusLabel(detailItem) }}
                </v-chip>
                <template v-else>—</template>
              </span>
            </div>

            <div
              v-if="detailItem.tags && detailItem.tags.length"
              class="ilm-sheet__row"
            >
              <span class="ilm-sheet__row-label">{{ $t("tag") }}</span>
              <span class="ilm-sheet__tags">
                <v-chip
                  v-for="(tag, ti) in detailItem.tags"
                  :key="ti"
                  :color="tag.color || '#607D8B'"
                  label
                  small
                  class="px-1 ms-1"
                >
                  <span class="px-1 white--text">{{ tag.text }}</span>
                </v-chip>
              </span>
            </div>
          </div>

        </div>
      </v-card>
    </v-bottom-sheet>

    <!-- Payment details layer (mobile-only, read-only): same item
         payments/checks the shared desktop dialog tables, as cards -->
    <v-bottom-sheet v-model="payedSheet" scrollable>
      <v-card class="ilm-sheet">
        <div class="ilm-sheet__handle" />
        <div class="ilm-sheet__header" dir="rtl">
          <div class="ilm-sheet__header-side">
            <span v-if="payedItem" class="ilm-sheet__num-chip">
              #{{ payedItem.number }}
            </span>
          </div>
          <div class="ilm-sheet__title">
            {{ $t("details") }} {{ $t("payments") }}
          </div>
          <div class="ilm-sheet__header-side ilm-sheet__header-side--end">
            <button
              type="button"
              class="ilm-sheet__back"
              :aria-label="$t('back')"
              @click="payedSheet = false"
            >
              <v-icon size="22" color="#37474F">mdi-arrow-left</v-icon>
            </button>
          </div>
        </div>

        <!-- Summary fixed under the header; only the payment cards scroll -->
        <div v-if="payedItem" class="ilm-pay-summary" dir="rtl">
          <div class="ilm-sheet__row">
            <span class="ilm-sheet__row-label">{{ $t("finalAmount") }}</span>
            <span class="ilm-sheet__row-value ilm-sheet__row-value--final">
              {{
                formatAmount(
                  payedItem,
                  payedItem.price ? payedItem.price.price : 0
                )
              }}
            </span>
          </div>
          <div class="ilm-sheet__row">
            <span class="ilm-sheet__row-label">{{ $t("payedPrice") }}</span>
            <span class="ilm-sheet__row-value ilm-sheet__row-value--paid">
              {{
                payedItem.payed
                  ? formatAmount(payedItem, payedItem.payed.amount || 0)
                  : "—"
              }}
            </span>
          </div>
          <div class="ilm-sheet__row">
            <span class="ilm-sheet__row-label">{{ $t("remainPrice") }}</span>
            <span
              class="ilm-sheet__row-value"
              :class="
                isRemain(payedItem)
                  ? 'ilm-sheet__row-value--due'
                  : 'ilm-sheet__row-value--paid'
              "
            >
              {{ remainText(payedItem) }}
            </span>
          </div>
        </div>

        <div v-if="payedItem" class="ilm-sheet__body" dir="rtl">
          <div v-if="!payedRows(payedItem).length" class="ilm-pay-empty">
            {{ "—" }}
          </div>
          <div
            v-for="(row, i) in payedRows(payedItem)"
            :key="'payed-' + i"
            class="ilm-pay-card"
          >
            <div class="ilm-pay-card__head">
              <span class="ilm-pay-card__index">{{ i + 1 }}</span>
              <span class="ilm-pay-card__type">{{ row.typeLabel }}</span>
            </div>
            <div class="ilm-sheet__row">
              <span class="ilm-sheet__row-label">{{ $t("amount") }}</span>
              <span class="ilm-sheet__row-value ilm-sheet__row-value--money">
                {{ formatAmount(payedItem, row.amount) }}
              </span>
            </div>
            <div class="ilm-sheet__row">
              <span class="ilm-sheet__row-label">{{ $t("date") }}</span>
              <span class="ilm-sheet__row-value">
                {{ row.date || "—" }}
              </span>
            </div>
            <div
              v-for="(line, li) in row.lines"
              :key="'payed-line-' + i + '-' + li"
              class="ilm-sheet__row"
            >
              <span class="ilm-sheet__row-label">{{ line.label }}</span>
              <span class="ilm-sheet__row-value">{{ line.value }}</span>
            </div>
          </div>
        </div>

        <div class="ilm-sheet__footer" dir="rtl">
          <button
            type="button"
            class="ilm-sheet__btn ilm-sheet__btn--primary"
            @click="payedSheet = false"
          >
            {{ $t("back") }}
          </button>
        </div>
      </v-card>
    </v-bottom-sheet>

    <!-- More-actions layer: every secondary row action from the details
         sheet, opened from the "more" quick tile (same shell methods) -->
    <v-bottom-sheet v-model="moreSheet">
      <v-card class="ilm-sheet">
        <div class="ilm-sheet__handle" />
        <div class="ilm-sheet__header" dir="rtl">
          <span class="ilm-sheet__header-spacer" />
          <div class="ilm-sheet__title">{{ $t("more") }}</div>
          <button
            type="button"
            class="ilm-sheet__back"
            :aria-label="$t('back')"
            @click="moreSheet = false"
          >
            <v-icon size="22" color="#37474F">mdi-arrow-left</v-icon>
          </button>
        </div>

        <div v-if="detailItem" class="ilm-sheet__body" dir="rtl">
          <div class="ilm-sheet__actions ilm-sheet__actions--sheet">
            <button
              type="button"
              class="ilm-sheet__action"
              @click="runMoreAction('barcode')"
            >
              <v-icon size="19" color="#5B7C99">mdi-barcode</v-icon>
              {{ $t("barcode") }}
            </button>
            <button
              v-show="
                invoiceList.type == 'sell' || invoiceList.type == 'presale'
              "
              type="button"
              class="ilm-sheet__action"
              @click="runMoreAction('editCategory')"
            >
              <v-icon size="19" color="#5B7C99">mdi-clipboard-edit</v-icon>
              {{ $t("editCasher") }}
            </button>
            <button
              type="button"
              class="ilm-sheet__action"
              @click="runMoreAction('copy')"
            >
              <v-icon size="19" color="#5B7C99">mdi-content-copy</v-icon>
              {{ $t("invoiceCopy") }}
            </button>
            <button
              type="button"
              class="ilm-sheet__action"
              v-clipboard:copy="
                $baseURL + '/' + invoiceList.type + '/edit/' + detailItem.uid
              "
              @click="onCopyLink"
            >
              <v-icon size="19" color="#5B7C99">mdi-link</v-icon>
              {{ $t("copy-link") }}
            </button>
            <button
              v-if="
                !invoiceList.documentPermmission &&
                detailItem.attachments &&
                detailItem.attachments.length > 0
              "
              type="button"
              class="ilm-sheet__action"
              @click="runMoreAction('documents')"
            >
              <v-icon size="19" color="#5B7C99">mdi-attachment</v-icon>
              {{ $t("documents") }}
            </button>
            <button
              v-if="detailItem.entrepot"
              type="button"
              class="ilm-sheet__action"
              @click="runMoreAction('entrepotPrint')"
            >
              <v-icon size="19" color="#5B7C99">mdi-warehouse</v-icon>
              {{ $t(detailItem.entrepot.type + "Print") }}
            </button>
            <div class="ilm-sheet__action ilm-sheet__action--log">
              <log
                :title-prop="$t('history')"
                icon-prop="mdi-history"
                :listProp="true"
                :type-prop="invoiceList.typeForm"
                :refIdProp="detailItem.id"
                mode-prop="button"
              />
            </div>
            <button
              v-show="
                detailItem.tax_payer &&
                detailItem.tax_payer.taxid &&
                detailItem.tax_payer.status == 'done'
              "
              type="button"
              class="ilm-sheet__action"
              :disabled="detailItem.lock ? true : false"
              @click="runMoreAction('editTax')"
            >
              <v-icon size="19" color="#5B7C99">mdi-note-edit-outline</v-icon>
              {{ $t("sendEditTaxInvoice") }}
            </button>
            <button
              v-show="
                removePermission(invoiceList.type) &&
                invoiceList.resolveAnnulmentBtnVisibility(detailItem)
              "
              type="button"
              class="ilm-sheet__action ilm-sheet__action--danger"
              :disabled="detailItem.lock ? true : false"
              @click="runMoreAction('annulment')"
            >
              <v-icon size="19" color="pink">mdi-close-circle-outline</v-icon>
              {{ $t("annulmentInvoice") }}
            </button>
          </div>
        </div>
      </v-card>
    </v-bottom-sheet>

    <!-- Mobile delete/annulment confirmation: same model as the composite
         delete sheet in invoice create (Composite.vue), plus one icon.
         Same shell remove flow as the desktop dialog. -->
    <v-bottom-sheet v-model="deleteSheet">
      <v-card class="ilm-confirm-sheet" dir="rtl">
        <div class="ilm-confirm-sheet__handle" />
        <div class="ilm-confirm-sheet__icon">
          <v-icon size="26" color="#E53935">
            {{
              invoiceList.removeType === "annulment"
                ? "mdi-close-circle-outline"
                : "mdi-trash-can-outline"
            }}
          </v-icon>
        </div>
        <div class="ilm-confirm-sheet__title">
          {{
            invoiceList.removeType === "annulment"
              ? $t("annulmentInvoice")
              : $t("delete")
          }}
        </div>
        <p class="ilm-confirm-sheet__subtitle">
          {{ $t("Are you sure you want to delete this item ?") }}
        </p>
        <div v-if="deleteGroupMode" class="ilm-confirm-sheet__rows">
          <div class="ilm-confirm-sheet__row">
            <span>{{ $t("totalCount") }}</span>
            <strong>{{ selectedItems.length }}</strong>
          </div>
          <!-- The exact invoices about to be deleted: number + account
               name, like the list, so the confirmation is fully informed -->
          <div class="ilm-confirm-sheet__items">
            <div
              v-for="(sel, si) in selectedItems"
              :key="'del-item-' + si"
              class="ilm-confirm-sheet__item"
            >
              <span class="ilm-confirm-sheet__item-main">
                <span class="ilm-confirm-sheet__item-index">
                  {{ selectedRowNumber(sel) }}
                </span>
                <span class="ilm-confirm-sheet__item-name">
                  {{ sel.customer ? sel.customer.name : "—" }}
                </span>
              </span>
              <span class="ilm-confirm-sheet__item-number">
                #{{ sel.number }}
              </span>
            </div>
          </div>
        </div>
        <div v-if="invoiceList.deleteItem" class="ilm-confirm-sheet__rows">
          <div
            v-if="invoiceList.deleteItem.customer"
            class="ilm-confirm-sheet__row"
          >
            <span>{{ $t("customer") }}</span>
            <strong>{{ invoiceList.deleteItem.customer.name }}</strong>
          </div>
          <div v-if="invoiceList.deleteItem.number" class="ilm-confirm-sheet__row">
            <span>{{ $t("number") }}</span>
            <strong>{{ invoiceList.deleteItem.number }}</strong>
          </div>
          <div v-if="invoiceList.deleteItem.date" class="ilm-confirm-sheet__row">
            <span>{{ $t("date") }}</span>
            <strong>{{ invoiceList.deleteItem.date }}</strong>
          </div>
          <div class="ilm-confirm-sheet__row">
            <span>{{ $t("finalAmount") }}</span>
            <strong>
              {{
                formatAmount(
                  invoiceList.deleteItem,
                  invoiceList.deleteItem.price
                    ? invoiceList.deleteItem.price.price
                    : 0
                )
              }}
            </strong>
          </div>
          <div class="ilm-confirm-sheet__row">
            <span>{{ $t("remainPrice") }}</span>
            <strong>{{ remainText(invoiceList.deleteItem) }}</strong>
          </div>
        </div>
        <div class="ilm-confirm-sheet__actions">
          <v-btn
            color="error"
            block
            depressed
            height="48"
            class="ilm-confirm-sheet__yes"
            @click="confirmDelete"
          >
            {{ $t("yes") }}
          </v-btn>
          <v-btn
            outlined
            block
            height="48"
            color="secondary"
            class="ilm-confirm-sheet__no"
            @click="deleteSheet = false"
          >
            {{ $t("no") }}
          </v-btn>
        </div>
      </v-card>
    </v-bottom-sheet>

    <!-- Mobile filter: layered like the invoice "specs" sheet in invoice
         create (InvoiceMobile metaSheet). Same shell filter state + getItems;
         only the presentation is re-executed here for mobile. -->
    <v-bottom-sheet v-model="filterSheet" scrollable>
      <v-card class="ilm-sheet ilm-sheet--filter">
        <div class="ilm-sheet__handle" />
        <div class="ilm-sheet__header" dir="rtl">
          <span class="ilm-sheet__header-spacer" />
          <div class="ilm-sheet__title">{{ $t("filter") }}</div>
          <button
            type="button"
            class="ilm-sheet__back"
            :aria-label="$t('back')"
            @click="filterSheet = false"
          >
            <v-icon size="22" color="#37474F">mdi-arrow-left</v-icon>
          </button>
        </div>

        <div class="ilm-filter-nav" dir="rtl">
          <button
            v-for="group in filterGroups"
            :key="group.key"
            type="button"
            class="ilm-filter-nav__item"
            @click="openFilterGroup(group.key)"
          >
            <v-icon class="ilm-filter-nav__icon" size="22" color="#5B7C99">
              {{ group.icon }}
            </v-icon>
            <span class="ilm-filter-nav__texts">
              <span class="ilm-filter-nav__item-title">{{ group.title }}</span>
              <span class="ilm-filter-nav__item-sub">
                {{ groupSubtitle(group.key) }}
              </span>
            </span>
            <span v-if="groupHasValue(group.key)" class="ilm-filter-nav__dot" />
            <v-icon size="20" color="#90A4AE">mdi-chevron-left</v-icon>
          </button>
        </div>

        <div class="ilm-sheet__footer" dir="rtl">
          <button
            type="button"
            class="ilm-sheet__btn ilm-sheet__btn--primary"
            @click="applyFilter"
          >
            <v-icon size="16" color="#FFFFFF">mdi-filter-outline</v-icon>
            {{ $t("filter") }}
          </button>
          <button
            type="button"
            class="ilm-sheet__btn ilm-sheet__btn--ghost"
            @click="clearFilter"
          >
            <v-icon size="16" color="#5B7C99">mdi-filter-remove-outline</v-icon>
            {{ $t("clear") }}
          </button>
        </div>
      </v-card>
    </v-bottom-sheet>

    <!-- Filter group layer (back returns to the filter main layer) -->
    <v-bottom-sheet v-model="filterGroupSheet" scrollable>
      <v-card class="ilm-sheet ilm-sheet--filter">
        <div class="ilm-sheet__handle" />
        <div class="ilm-sheet__header" dir="rtl">
          <span class="ilm-sheet__header-spacer" />
          <div class="ilm-sheet__title">{{ activeFilterGroupTitle }}</div>
          <button
            type="button"
            class="ilm-sheet__back"
            :aria-label="$t('back')"
            @click="backToFilterMain"
          >
            <v-icon size="22" color="#37474F">mdi-arrow-left</v-icon>
          </button>
        </div>

        <div class="ilm-filter-fields" dir="rtl">
          <template v-if="filterGroup === 'customer'">
            <div class="ilm-f-field-wrap">
              <div class="ilm-f-field-wrap__label">{{ $t("customer") }}</div>
              <button
                type="button"
                class="ilm-f-picker-field"
                @click="openPicker('acAccount')"
              >
                <span class="ilm-f-picker-field__text">
                  {{ pickerSummary("acAccount", invoiceList.account) }}
                </span>
                <v-icon size="20" color="#78909C">mdi-chevron-down</v-icon>
              </button>
              <div class="ilm-picker-host" aria-hidden="true">
                <autocomplete
                  ref="acAccount"
                  v-model="invoiceList.account"
                  :title="$t('customer')"
                  type="person"
                  :filter="true"
                  :multiple="true"
                />
              </div>
            </div>
            <div class="ilm-f-field-wrap">
              <div class="ilm-f-field-wrap__label">{{ $t("currency") }}</div>
              <button
                type="button"
                class="ilm-f-picker-field"
                @click="openPicker('acCurrency')"
              >
                <span class="ilm-f-picker-field__text">
                  {{ pickerSummary("acCurrency", invoiceList.currencyFilter) }}
                </span>
                <v-icon size="20" color="#78909C">mdi-chevron-down</v-icon>
              </button>
              <div class="ilm-picker-host" aria-hidden="true">
                <autocomplete
                  ref="acCurrency"
                  v-model="invoiceList.currencyFilter"
                  :title="$t('currency')"
                  type="currency"
                  :filter="true"
                  :multiple="true"
                />
              </div>
            </div>
            <div class="ilm-f-field-wrap">
              <div class="ilm-f-field-wrap__label">{{ $t("registerar") }}</div>
              <button
                type="button"
                class="ilm-f-picker-field"
                @click="openPicker('acRegisterar')"
              >
                <span class="ilm-f-picker-field__text">
                  {{ pickerSummary("acRegisterar", invoiceList.registerar) }}
                </span>
                <v-icon size="20" color="#78909C">mdi-chevron-down</v-icon>
              </button>
              <div class="ilm-picker-host" aria-hidden="true">
                <autocomplete
                  ref="acRegisterar"
                  v-model="invoiceList.registerar"
                  :title="$t('registerar')"
                  type="partners"
                  :multiple="true"
                  :filter="true"
                />
              </div>
            </div>
          </template>

          <template v-else-if="filterGroup === 'amountNumber'">
            <div
              v-for="field in numpadFields"
              :key="field.key"
              class="ilm-f-field-wrap"
            >
              <div class="ilm-f-field-wrap__label">{{ field.label }}</div>
              <button
                type="button"
                class="ilm-f-picker-field"
                @click="openNumpad(field)"
              >
                <span class="ilm-f-picker-field__text">
                  {{ numpadFieldText(field.key) }}
                </span>
                <span
                  v-if="hasValue(invoiceList[field.key])"
                  class="ilm-picker-cover__clear"
                  @click.stop="invoiceList[field.key] = null"
                >
                  <v-icon size="18" color="#90A4AE">mdi-close</v-icon>
                </span>
                <v-icon size="20" color="#78909C">mdi-chevron-down</v-icon>
              </button>
            </div>
          </template>

          <template v-else-if="filterGroup === 'date'">
            <div
              v-for="field in dateFields"
              :key="field.key"
              class="ilm-f-field-wrap"
            >
              <div class="ilm-f-field-wrap__label">{{ field.label }}</div>
              <button
                type="button"
                class="ilm-f-picker-field"
                @click="openDateLayer(field)"
              >
                <span class="ilm-f-picker-field__text">
                  {{
                    hasValue(invoiceList[field.key])
                      ? invoiceList[field.key]
                      : "—"
                  }}
                </span>
                <v-icon size="20" color="#78909C">
                  {{ field.isTime ? "mdi-clock-outline" : "mdi-calendar" }}
                </v-icon>
              </button>
            </div>
          </template>

          <template v-else-if="filterGroup === 'category'">
            <div class="ilm-f-field-wrap">
              <div class="ilm-f-field-wrap__label">{{ $t("tag") }}</div>
              <button
                type="button"
                class="ilm-f-picker-field"
                @click="openPicker('acTag')"
              >
                <span class="ilm-f-picker-field__text">
                  {{ pickerSummary("acTag", invoiceList.tag) }}
                </span>
                <v-icon size="20" color="#78909C">mdi-chevron-down</v-icon>
              </button>
              <div class="ilm-picker-host" aria-hidden="true">
                <tag ref="acTag" v-model="invoiceList.tag" />
              </div>
            </div>
            <div class="ilm-f-field-wrap">
              <div class="ilm-f-field-wrap__label">{{ $t("category") }}</div>
              <button
                type="button"
                class="ilm-f-picker-field"
                @click="openPicker('acCategory')"
              >
                <span class="ilm-f-picker-field__text">
                  {{ pickerSummary("acCategory", invoiceList.category) }}
                </span>
                <v-icon size="20" color="#78909C">mdi-chevron-down</v-icon>
              </button>
              <div class="ilm-picker-host" aria-hidden="true">
                <autocomplete
                  ref="acCategory"
                  v-model="invoiceList.category"
                  :title="$t('category')"
                  type="accountCategory"
                  :filter="true"
                  :multiple="true"
                />
              </div>
            </div>
            <div class="ilm-f-field-wrap">
              <div class="ilm-f-field-wrap__label">{{ $t("branch") }}</div>
              <button
                type="button"
                class="ilm-f-picker-field"
                @click="openPicker('acBranch')"
              >
                <span class="ilm-f-picker-field__text">
                  {{ pickerSummary("acBranch", invoiceList.branch) }}
                </span>
                <v-icon size="20" color="#78909C">mdi-chevron-down</v-icon>
              </button>
              <div class="ilm-picker-host" aria-hidden="true">
                <autocomplete
                  ref="acBranch"
                  v-model="invoiceList.branch"
                  title="branch"
                  type="branch"
                  :filter="true"
                  :multiple="true"
                />
              </div>
            </div>
            <div class="ilm-f-field-wrap">
              <div class="ilm-f-field-wrap__label">{{ $t("entrepot") }}</div>
              <button
                type="button"
                class="ilm-f-picker-field"
                @click="openPicker('acEntrepot')"
              >
                <span class="ilm-f-picker-field__text">
                  {{ pickerSummary("acEntrepot", invoiceList.entrepot) }}
                </span>
                <v-icon size="20" color="#78909C">mdi-chevron-down</v-icon>
              </button>
              <div class="ilm-picker-host" aria-hidden="true">
                <autocomplete
                  ref="acEntrepot"
                  v-model="invoiceList.entrepot"
                  title="entrepot"
                  type="entrepot"
                  :filter="true"
                  :multiple="true"
                />
              </div>
            </div>
          </template>

          <template v-else-if="filterGroup === 'status'">
            <div class="ilm-f-field-wrap">
              <div class="ilm-f-field-wrap__label">{{ $t("status") }}</div>
              <div class="ilm-picker-overlay">
                <status
                  ref="fStatus"
                  class="ilm-f-field"
                  v-model="invoiceList.sellStatus"
                  :hideconfig="true"
                />
                <button
                  type="button"
                  class="ilm-f-picker-field ilm-picker-cover"
                  @click="openStatusPicker"
                >
                  <span class="ilm-f-picker-field__text">
                    {{ statusSummary() }}
                  </span>
                  <span
                    v-if="hasValue(invoiceList.sellStatus)"
                    class="ilm-picker-cover__clear"
                    @click.stop="invoiceList.sellStatus = null"
                  >
                    <v-icon size="18" color="#90A4AE">mdi-close</v-icon>
                  </span>
                  <v-icon size="20" color="#78909C">mdi-chevron-down</v-icon>
                </button>
              </div>
            </div>
            <div class="ilm-f-field-wrap">
              <div class="ilm-f-field-wrap__label">
                {{ $t("lockStatusDocuments") }}
              </div>
              <button
                type="button"
                class="ilm-f-picker-field"
                @click="
                  openSelectLayer(
                    'lockStatus',
                    $t('lockStatusDocuments'),
                    invoiceList.lockStatusItems,
                    'text'
                  )
                "
              >
                <span class="ilm-f-picker-field__text">
                  {{
                    selectSummary(
                      "lockStatus",
                      invoiceList.lockStatusItems,
                      "text"
                    )
                  }}
                </span>
                <v-icon size="20" color="#78909C">mdi-chevron-down</v-icon>
              </button>
            </div>
            <div class="ilm-f-field-wrap">
              <div class="ilm-f-field-wrap__label">
                {{ $t("payed-status") }}
              </div>
              <button
                type="button"
                class="ilm-f-picker-field"
                @click="
                  openSelectLayer(
                    'payedStatus',
                    $t('payed-status'),
                    invoiceList.payedStatuses,
                    'label'
                  )
                "
              >
                <span class="ilm-f-picker-field__text">
                  {{
                    selectSummary(
                      "payedStatus",
                      invoiceList.payedStatuses,
                      "label"
                    )
                  }}
                </span>
                <v-icon size="20" color="#78909C">mdi-chevron-down</v-icon>
              </button>
            </div>
          </template>

          <!-- Custom fields: same model as invoice create custom-fields sheet,
               writing the same shell customFields [{key, value}] -->
          <template v-else-if="filterGroup === 'custom'">
            <div class="ilm-cf__hint">
              {{ $t("invoice-mobile-custom-fields-hint") }}
            </div>

            <button
              type="button"
              class="ilm-cf__add"
              @click="addFilterCustomField"
            >
              <v-icon size="18" color="#5B7C99">mdi-plus</v-icon>
              <span>{{ $t("addCustomeField") }}</span>
            </button>

            <div
              v-if="!filterCustomItems.length"
              class="ilm-cf__empty"
            >
              {{ $t("invoice-mobile-custom-fields-empty") }}
            </div>

            <div
              v-for="(item, index) in filterCustomItems"
              :key="'filter-custom-' + index"
              class="ilm-cf-card"
            >
              <div class="ilm-cf-card__type">
                <div class="ilm-f-field-wrap ilm-cf-card__type-field">
                  <div class="ilm-f-field-wrap__label">
                    {{ $t("additionalFields") }}
                  </div>
                  <v-combobox
                    v-model="item.key"
                    :items="filterCustomKeyItems"
                    :search-input.sync="filterCustomSearch"
                    solo
                    flat
                    dense
                    hide-details
                    hide-selected
                    height="40"
                    append-icon="mdi-chevron-down"
                    class="ilm-f-field"
                    @change="syncFilterCustomFields"
                  >
                    <template v-slot:no-data>
                      <v-list-item>
                        <v-list-item-content>
                          <v-list-item-title>
                            {{ $t("toCreate") }}: "<strong>{{
                              filterCustomSearch
                            }}</strong
                            >". {{ $t("doEnter") }}
                          </v-list-item-title>
                        </v-list-item-content>
                      </v-list-item>
                    </template>
                  </v-combobox>
                </div>
                <button
                  type="button"
                  class="ilm-cf-card__remove"
                  :aria-label="$t('remove')"
                  @click="removeFilterCustomField(index)"
                >
                  <v-icon size="20" color="#E91E63">mdi-close</v-icon>
                </button>
              </div>

              <div class="ilm-f-field-wrap">
                <div class="ilm-f-field-wrap__label">{{ $t("value") }}</div>
                <input
                  v-model="item.value"
                  type="text"
                  class="ilm-cf-card__input ilm-cf-card__input--text"
                  @input="syncFilterCustomFields"
                />
              </div>
            </div>
          </template>
        </div>

        <div class="ilm-sheet__footer" dir="rtl">
          <button
            type="button"
            class="ilm-sheet__btn ilm-sheet__btn--primary"
            @click="backToFilterMain"
          >
            {{ $t("Save") }}
          </button>
        </div>
      </v-card>
    </v-bottom-sheet>

    <!-- Date/time layer: same inline wheel picker as invoice create
         (metaDateSheet in InvoiceMobile), bound to the same shell fields -->
    <v-bottom-sheet v-model="dateLayerOpen">
      <v-card class="ilm-sheet ilm-sheet--filter">
        <div class="ilm-sheet__handle" />
        <div class="ilm-sheet__header" dir="rtl">
          <span class="ilm-sheet__header-spacer" />
          <div class="ilm-sheet__title">{{ dateLayerLabel }}</div>
          <button
            type="button"
            class="ilm-sheet__back"
            :aria-label="$t('back')"
            @click="dateLayerOpen = false"
          >
            <v-icon size="22" color="#37474F">mdi-arrow-left</v-icon>
          </button>
        </div>

        <div class="ilm-mobile-date" dir="rtl">
          <div class="ilm-mobile-date__selected">
            <span class="ilm-mobile-date__label">{{ dateLayerLabel }}</span>
            <strong class="ilm-mobile-date__value">
              {{
                hasValue(invoiceList[dateLayerField])
                  ? invoiceList[dateLayerField]
                  : "—"
              }}
            </strong>
            <button
              v-if="hasValue(invoiceList[dateLayerField])"
              type="button"
              class="ilm-mobile-date__clear"
              @click="invoiceList[dateLayerField] = null"
            >
              {{ $t("clear") }}
            </button>
          </div>
          <date-picker
            v-if="dateLayerField"
            :key="dateLayerField"
            v-model="invoiceList[dateLayerField]"
            inline
            simple
            auto-submit
            :type="dateLayerIsTime ? 'time' : 'date'"
            :locale="$store.state.date_locale"
            :format="dateLayerIsTime ? 'HH:mm' : $store.state.date_format"
            :display-format="dateLayerIsTime ? 'HH' : undefined"
            :min="dateLayerMin"
            class="ilm-mobile-date__picker"
            color="#7B8BA3"
          />
        </div>

        <div class="ilm-sheet__footer" dir="rtl">
          <button
            type="button"
            class="ilm-sheet__btn ilm-sheet__btn--primary"
            @click="dateLayerOpen = false"
          >
            {{ $t("Save") }}
          </button>
        </div>
      </v-card>
    </v-bottom-sheet>

    <!-- Single-select layer for lock / payed status filter fields -->
    <v-bottom-sheet v-model="selectLayerOpen">
      <v-card class="ilm-sheet ilm-sheet--filter">
        <div class="ilm-sheet__handle" />
        <div class="ilm-sheet__header" dir="rtl">
          <span class="ilm-sheet__header-spacer" />
          <div class="ilm-sheet__title">{{ selectLayerTitle }}</div>
          <button
            type="button"
            class="ilm-sheet__back"
            :aria-label="$t('back')"
            @click="selectLayerOpen = false"
          >
            <v-icon size="22" color="#37474F">mdi-arrow-left</v-icon>
          </button>
        </div>

        <div class="ilm-select-layer" dir="rtl">
          <button
            v-for="item in selectLayerItems"
            :key="item.value"
            type="button"
            class="ilm-select-layer__item"
            @click="selectLayerPick(item.value)"
          >
            <span class="ilm-select-layer__text">
              {{ item[selectLayerTextKey] }}
            </span>
            <v-icon
              v-if="
                selectLayerField &&
                invoiceList[selectLayerField] === item.value
              "
              size="20"
              color="#5B7C99"
            >
              mdi-check
            </v-icon>
          </button>
          <button
            v-if="selectLayerField && hasValue(invoiceList[selectLayerField])"
            type="button"
            class="ilm-select-layer__clear"
            @click="selectLayerPick(null)"
          >
            {{ $t("clear") }}
          </button>
        </div>
      </v-card>
    </v-bottom-sheet>

    <!-- Numpad layer for numeric filter fields (same model as invoice create) -->
    <v-bottom-sheet v-model="numpadOpen" @click:outside="numpadOpen = false">
      <v-card class="ilm-numpad" dir="rtl">
        <div class="ilm-sheet__handle" />
        <div class="ilm-numpad__header">
          <span class="ilm-numpad__header-spacer" />
          <div class="ilm-numpad__title">{{ numpadLabel }}</div>
          <button
            type="button"
            class="ilm-sheet__back ilm-numpad__back"
            :aria-label="$t('back')"
            @click="numpadOpen = false"
          >
            <v-icon size="22" color="#37474F">mdi-arrow-left</v-icon>
          </button>
        </div>

        <div class="ilm-numpad__display">
          <div class="ilm-numpad__display-label">{{ numpadLabel }}</div>
          <div class="ilm-numpad__display-value">{{ numpadDisplay }}</div>
        </div>

        <div class="ilm-numpad__pad" dir="ltr">
          <div class="ilm-numpad__digits">
            <button
              v-for="n in [7, 8, 9, 4, 5, 6, 1, 2, 3]"
              :key="'d-' + n"
              type="button"
              class="ilm-numpad__key"
              @click="numpadDigit(n)"
            >
              {{ n }}
            </button>
            <button
              type="button"
              class="ilm-numpad__key ilm-numpad__key--multi"
              @click="numpadAppend('00')"
            >
              00
            </button>
            <button
              type="button"
              class="ilm-numpad__key"
              @click="numpadDigit(0)"
            >
              0
            </button>
            <button
              type="button"
              class="ilm-numpad__key ilm-numpad__key--multi"
              @click="numpadAppend('000')"
            >
              000
            </button>
          </div>
          <div class="ilm-numpad__actions">
            <button
              type="button"
              class="ilm-numpad__key ilm-numpad__key--action"
              @click="numpadStep(1)"
            >
              +
            </button>
            <button
              type="button"
              class="ilm-numpad__key ilm-numpad__key--action"
              @click="numpadStep(-1)"
            >
              -
            </button>
            <button
              type="button"
              class="ilm-numpad__key ilm-numpad__key--action"
              @click="numpadClear"
            >
              C
            </button>
            <button
              type="button"
              class="ilm-numpad__key ilm-numpad__key--action"
              @click="numpadDot"
            >
              .
            </button>
            <button
              type="button"
              class="ilm-numpad__key ilm-numpad__key--action"
              @click="numpadBackspace"
            >
              <v-icon size="20" color="#5B7C99">mdi-backspace-outline</v-icon>
            </button>
          </div>
        </div>

        <div class="ilm-numpad__footer">
          <button
            type="button"
            class="ilm-numpad__confirm"
            @click="confirmNumpad"
          >
            {{ $t("confirm") }}
          </button>
        </div>
      </v-card>
    </v-bottom-sheet>

    <!-- Bulk action bar (selection mode): main group actions up front,
         the rest under "more" — all call the same shell group methods
         after the local selection is synced into invoiceList.selected -->
    <div v-if="selectionMode" class="ilm-bulkbar" dir="rtl">
      <button
        v-if="lockPermission"
        type="button"
        class="ilm-bulkbar__btn"
        :disabled="!selectedItems.length"
        @click="bulkLock('lock')"
      >
        <v-icon size="19" color="#5B7C99">mdi-lock-outline</v-icon>
        {{ $t("lock") }}
      </button>
      <button
        v-if="lockPermission"
        type="button"
        class="ilm-bulkbar__btn"
        :disabled="!selectedItems.length"
        @click="bulkLock('unlock')"
      >
        <v-icon size="19" color="#5B7C99">mdi-lock-open-outline</v-icon>
        {{ $t("unlock") }}
      </button>
      <button
        v-if="invoiceList.type !== 'removedInvoices'"
        type="button"
        class="ilm-bulkbar__btn"
        :disabled="!selectedItems.length"
        @click="bulkGroupPrint"
      >
        <v-icon size="19" color="#5B7C99">mdi-printer-settings</v-icon>
        {{ $t("groupPrint") }}
      </button>
      <button
        v-if="removePermission(invoiceList.type)"
        type="button"
        class="ilm-bulkbar__btn ilm-bulkbar__btn--danger"
        :disabled="!selectedItems.length"
        @click="askGroupDelete"
      >
        <v-icon size="19" color="#E53935">mdi-trash-can-outline</v-icon>
        {{ $t("groupDelete") }}
      </button>
      <button
        type="button"
        class="ilm-bulkbar__btn"
        :disabled="!selectedItems.length"
        @click="bulkMoreSheet = true"
      >
        <v-icon size="19" color="#5B7C99">mdi-dots-horizontal</v-icon>
        {{ $t("more") }}
      </button>
    </div>

    <!-- Bulk "more" layer: check average + product barcode + tax payer -->
    <v-bottom-sheet v-model="bulkMoreSheet">
      <v-card class="ilm-sheet">
        <div class="ilm-sheet__handle" />
        <div class="ilm-sheet__header" dir="rtl">
          <span class="ilm-sheet__header-spacer" />
          <div class="ilm-sheet__title">{{ $t("more") }}</div>
          <button
            type="button"
            class="ilm-sheet__back"
            :aria-label="$t('back')"
            @click="bulkMoreSheet = false"
          >
            <v-icon size="22" color="#37474F">mdi-arrow-left</v-icon>
          </button>
        </div>

        <div class="ilm-sheet__body" dir="rtl">
          <div class="ilm-sheet__actions ilm-sheet__actions--sheet">
            <button
              type="button"
              class="ilm-sheet__action"
              @click="bulkCheckAverage"
            >
              <v-icon size="19" color="#5B7C99">
                mdi-calendar-clock-outline
              </v-icon>
              {{ $t("checkAverage") }}
            </button>
            <button
              type="button"
              class="ilm-sheet__action"
              @click="bulkProductBarcode"
            >
              <v-icon size="19" color="#5B7C99">mdi-barcode</v-icon>
              {{ $t("productBarcode") }}
            </button>
            <button
              v-if="
                invoiceList.type === 'sell' &&
                $store.state.permissions &&
                $store.state.permissions.includes('reviewDigikalaOrder') &&
                digikalaPermission
              "
              type="button"
              class="ilm-sheet__action"
              @click="bulkReviewDigikala"
            >
              <v-icon size="19" color="#5B7C99">mdi-shopping-search-outline</v-icon>
              {{ $t("reviewDigikalaOrder") }}
            </button>
            <button
              v-if="
                invoiceList.taxPayerSystem &&
                (invoiceList.type == 'sell' || invoiceList.type == 'returnSell')
              "
              type="button"
              class="ilm-sheet__action"
              @click="bulkTaxPayer"
            >
              <v-icon size="19" color="#5B7C99">mdi-cash</v-icon>
              {{ $t("send to tax payer system") }}
            </button>
          </div>
        </div>
      </v-card>
    </v-bottom-sheet>

    <!-- Shared inner-component dialog (person edit / turnover / ...):
         same instance the desktop view renders for shell openComponent -->
    <dialog-component
      :dialog-prop="invoiceList.innerComponent"
      :data-prop="invoiceList.dialogData"
      @close="invoiceList.innerComponent = false"
    />

    <!-- infinite-scroll sentinel: loads next page with the same options/getItems -->
    <div ref="sentinel" class="ilm__sentinel" />
  </div>
</template>

<script>
export default {
  name: "InvoiceListMobile",
  inject: ["invoiceList"],
  components: {
    // Same filter field components the shell registers for the desktop dialog
    autocomplete: () =>
      import("../../../../components/general/patial/autocomplete"),
    Tag: () => import("../../../../components/general/Tag/tag"),
    Status: () => import("../../../../components/general/Status/status"),
    // Same inner-component dialog the shell registers (list.vue) so
    // openComponent('account' | 'accountingTurnover', id) renders on mobile too
    dialogComponent: () => import("../../../../components/general/dialog/index"),
  },
  data() {
    return {
      displayItems: [],
      appending: false,
      observer: null,
      detailSheet: false,
      moreSheet: false,
      detailItem: null,
      payedSheet: false,
      payedItem: null,
      selectionMode: false,
      selectedItems: [],
      bulkMoreSheet: false,
      deleteGroupMode: false,
      deleteSheet: false,
      filterSheet: false,
      filterGroupSheet: false,
      filterGroup: null,
      numpadOpen: false,
      numpadField: null,
      numpadLabel: "",
      numpadDraft: "",
      numpadIsAmount: false,
      dateLayerOpen: false,
      dateLayerField: null,
      dateLayerLabel: "",
      dateLayerIsTime: false,
      selectLayerOpen: false,
      selectLayerField: null,
      selectLayerTitle: "",
      selectLayerItems: [],
      selectLayerTextKey: "text",
      filterCustomItems: [],
      filterCustomKeyItems: [],
      filterCustomSearch: null,
      filterCustomKeyTimer: null,
    };
  },
  computed: {
    currentPage() {
      return (this.invoiceList.options && this.invoiceList.options.page) || 1;
    },
    filterGroups() {
      const groups = [
        {
          key: "customer",
          icon: "mdi-account-outline",
          title: this.$t("customer"),
        },
        {
          key: "amountNumber",
          icon: "mdi-numeric",
          title: this.$t("amount") + " / " + this.$t("number"),
        },
        { key: "date", icon: "mdi-calendar", title: this.$t("date") },
        {
          key: "category",
          icon: "mdi-shape-outline",
          title: this.$t("category"),
        },
        {
          key: "status",
          icon: "mdi-check-circle-outline",
          title: this.$t("status"),
        },
      ];
      if (this.customFieldsPermission) {
        groups.push({
          key: "custom",
          icon: "mdi-playlist-plus",
          title: this.$t("customFields"),
        });
      }
      return groups;
    },
    activeFilterGroupTitle() {
      const group = this.filterGroups.find((g) => g.key === this.filterGroup);
      return group ? group.title : this.$t("filter");
    },
    numpadFields() {
      return [
        {
          key: "fromNumber",
          label: this.$t("from") + " " + this.$t("number"),
        },
        {
          key: "toNumber",
          label: this.$t("until") + " " + this.$t("number"),
        },
        {
          key: "fromAmount",
          label: this.$t("from") + " " + this.$t("amount"),
          isAmount: true,
        },
        {
          key: "toAmount",
          label: this.$t("until") + " " + this.$t("amount"),
          isAmount: true,
        },
      ];
    },
    dateFields() {
      return [
        {
          key: "fromDate",
          label: this.$t("from") + " " + this.$t("date"),
          isTime: false,
        },
        {
          key: "toDate",
          label: this.$t("until") + " " + this.$t("date"),
          isTime: false,
        },
        { key: "fromDueDate", label: this.$t("fromDueDate"), isTime: false },
        { key: "toDueDate", label: this.$t("toDueDate"), isTime: false },
        { key: "fromTime", label: this.$t("from-hour"), isTime: true },
        { key: "toTime", label: this.$t("to-hour"), isTime: true },
      ];
    },
    dateLayerMin() {
      if (this.dateLayerIsTime || !this.dateLayerField) return undefined;
      if (
        this.dateLayerField === "toDate" ||
        this.dateLayerField === "toDueDate"
      ) {
        return this.invoiceList.fromDate || undefined;
      }
      return undefined;
    },
    numpadDisplay() {
      const raw = String(this.numpadDraft == null ? "" : this.numpadDraft);
      if (!raw) return "0";
      // Invoice numbers are not amounts: no thousand separators, raw digits.
      if (!this.numpadIsAmount) return raw;
      if (raw.endsWith(".")) {
        const base = raw.slice(0, -1) || "0";
        const formatted = this.$numberFormat
          ? this.$numberFormat(base, "plain", 0)
          : base;
        return formatted + ".";
      }
      return this.$numberFormat ? this.$numberFormat(raw, "plain", 0) : raw;
    },
    pickerColor() {
      return this.$vuetify.theme.themes[
        this.$vuetify.theme.isDark ? "dark" : "light"
      ].primary;
    },
  },
  watch: {
    "invoiceList.items"(val) {
      const items = val || [];
      if (this.appending) {
        const seen = {};
        this.displayItems.forEach((i) => {
          seen[i.id] = true;
        });
        this.displayItems = this.displayItems.concat(
          items.filter((i) => !seen[i.id])
        );
        this.appending = false;
      } else {
        this.displayItems = items.slice();
        // Fresh list (search/filter/refresh): selections no longer apply,
        // same reset the shell does on its own `selected`.
        this.selectedItems = [];
      }
    },
    filterCustomSearch() {
      this.loadFilterCustomKeys();
    },
    "invoiceList.search"() {
      this.appending = false;
      if (this.currentPage !== 1) {
        this.invoiceList.options = Object.assign({}, this.invoiceList.options, {
          page: 1,
        });
      }
    },
  },
  mounted() {
    this.displayItems = (this.invoiceList.items || []).slice();
    // Desktop table used to set page/itemsPerPage; do it explicitly for mobile:
    // first load = page 1 with 10 items, then lazy-load 10 per scroll.
    // Assigning options triggers the shell options watcher -> getItems (same as desktop).
    this.invoiceList.options = Object.assign({}, this.invoiceList.options, {
      page: 1,
      itemsPerPage: 10,
    });
    this.$nextTick(() => {
      if (!("IntersectionObserver" in window) || !this.$refs.sentinel) return;
      this.observer = new IntersectionObserver(
        (entries) => {
          if (entries[0] && entries[0].isIntersecting) this.loadMore();
        },
        { rootMargin: "200px" }
      );
      this.observer.observe(this.$refs.sentinel);
    });
  },
  beforeDestroy() {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
  },
  methods: {
    goBack() {
      if (window.history.length > 1) {
        this.$router.back();
        return;
      }
      this.$router.push({ name: "Home" }).catch(() => {});
    },
    formatAmount(item, value) {
      const decimal = item.currency ? item.currency.decimal : 0;
      const symbol = item.currency ? item.currency.symbol : "";
      return this.$numberFormat(value || 0, decimal) + (symbol ? " " + symbol : "");
    },
    isRemain(item) {
      if (!item.payed) return false;
      return this.invoiceList.invoiceListPayedIsRemain(item);
    },
    hasPaymentDetails(item) {
      if (
        item.payed &&
        (parseFloat(item.payed.paymentsAmount || 0) > 0 ||
          parseFloat(item.payed.checksAmount || 0) > 0)
      ) {
        return true;
      }
      return this.paymentCount(item) > 0;
    },
    paymentCount(item) {
      const payments = (item && item.payments) || [];
      const checks = (item && item.checks) || [];
      return payments.length + checks.length;
    },
    openPayedSheet(item) {
      // Mobile-only read-only layer; desktop keeps the shared dialog.
      this.payedItem = item;
      this.payedSheet = true;
    },
    payedRows(item) {
      // Same derivation as the shared PayedDetailsDialog table rows:
      // payments first, then checks, straight from the list item.
      const rows = [];
      const payments = (item && item.payments) || [];
      payments.forEach((entry) => {
        const payment = entry.payments || entry;
        if (!payment) return;
        const bank = payment.bank || {};
        const lines = [];
        if (bank.name) lines.push({ label: this.$t("bank"), value: bank.name });
        rows.push({
          typeLabel: this.payedTypeLabel(payment, bank),
          date: this.payedDateOnly(payment.date),
          amount: parseFloat(payment.amount || 0),
          lines,
        });
      });
      const checks = (item && item.checks) || [];
      checks.forEach((entry) => {
        const check = entry.checks || entry;
        if (!check) return;
        const lines = [];
        const bankName =
          (check.bank && check.bank.name) || check.bank_name || check.bankName;
        if (bankName) lines.push({ label: this.$t("bank"), value: bankName });
        if (check.number) {
          lines.push({ label: this.$t("check-number"), value: check.number });
        }
        const fishermanNumber =
          check.fisherman_number || check.fishermanNumber;
        if (fishermanNumber) {
          lines.push({
            label: this.$t("fishermanNumber"),
            value: fishermanNumber,
          });
        }
        rows.push({
          typeLabel: this.$t("check"),
          date: this.payedDateOnly(
            check.date || check.due_date || check.dueDate || check.time
          ),
          amount: parseFloat(check.amount || 0),
          lines,
        });
      });
      return rows;
    },
    payedTypeLabel(payment, bank) {
      if (bank && bank.type === "cash") return this.$t("cash-format");
      if (payment && payment.type) return this.$t(payment.type);
      return this.$t("cash");
    },
    payedDateOnly(value) {
      // Same trimming as PayedDetailsDialog: keep the date, drop the time.
      if (!value) return null;
      const text = String(value).trim();
      const dashSplit = text.split(/\s+-\s+/);
      if (
        dashSplit.length > 1 &&
        /[:\d۰-۹]/.test(dashSplit[dashSplit.length - 1])
      ) {
        return dashSplit.slice(0, -1).join(" - ").trim();
      }
      return text.replace(/\s+\d{1,2}:\d{2}(:\d{2})?$/, "").trim();
    },
    remainText(item) {
      if (!item.payed) return "—";
      if (this.isRemain(item)) {
        return this.formatAmount(
          item,
          this.invoiceList.invoiceListPayedRemain(item)
        );
      }
      return this.$t("payed");
    },
    loadMore() {
      if (!this.displayItems.length) return;
      if (this.invoiceList.loading || this.appending) return;
      if (this.currentPage >= (this.invoiceList.pageCount || 1)) return;
      this.appending = true;
      // Same mechanism as desktop table: options change -> shell watcher -> getItems
      this.invoiceList.options = Object.assign({}, this.invoiceList.options, {
        page: this.currentPage + 1,
      });
    },
    onRefreshTap() {
      // Refresh means "load the list fresh from the top": reset pagination
      // to page 1 (same reset as applyFilter), then reload via the shell
      // getItems and scroll back to the top of the list.
      this.appending = false;
      this.invoiceList.options = Object.assign({}, this.invoiceList.options, {
        page: 1,
      });
      this.invoiceList.getItems();
      window.scrollTo({ top: 0, behavior: "smooth" });
    },
    onFilterTap() {
      // Mobile-only filter layer (same shell filter state as desktop dialog).
      this.filterSheet = true;
    },
    openPicker(refName) {
      // Entity fields are picker-field buttons (like the invoice specs
      // sheets); the tap opens the component's own mobile picker, so the
      // same selection dialog / state is reused — only the box changed.
      const comp = this.$refs[refName];
      if (comp && typeof comp.openMobileDialog === "function") {
        comp.openMobileDialog();
      }
    },
    openStatusPicker() {
      // The status combobox stays mounted under the picker-field cover;
      // tapping the cover opens the combobox's own menu (same items and
      // model as desktop — only the visible box changed).
      const comp = this.$refs.fStatus;
      const combo = comp && comp.$refs ? comp.$refs.combo : null;
      if (combo) {
        if (typeof combo.focus === "function") combo.focus();
        combo.isMenuActive = true;
      }
    },
    statusSummary() {
      const v = this.invoiceList.sellStatus;
      if (!this.hasValue(v)) return "—";
      if (typeof v === "object") return v.name || v.text || "—";
      const comp = this.$refs.fStatus;
      const items = (comp && comp.items) || [];
      const found = items.find((i) => i && i.id === v);
      return found && found.name ? found.name : "—";
    },
    openSelectLayer(field, title, items, textKey) {
      this.selectLayerField = field;
      this.selectLayerTitle = title;
      this.selectLayerItems = items || [];
      this.selectLayerTextKey = textKey || "text";
      this.selectLayerOpen = true;
    },
    selectLayerPick(value) {
      if (this.selectLayerField) {
        this.invoiceList[this.selectLayerField] = value;
      }
      this.selectLayerOpen = false;
    },
    selectSummary(field, items, textKey) {
      const v = this.invoiceList[field];
      if (!this.hasValue(v)) return "—";
      const found = (items || []).find((i) => i && i.value === v);
      return found ? found[textKey] : "—";
    },
    pickerSummary(refName, value) {
      if (!this.hasValue(value)) return "—";
      const comp = this.$refs[refName];
      const items = (comp && comp.items) || [];
      const arr = Array.isArray(value) ? value : [value];
      const names = [];
      arr.forEach((v) => {
        if (v && typeof v === "object") {
          if (v.name || v.text) names.push(v.name || v.text);
        } else {
          const found = items.find((i) => i && i.id === v);
          if (found && found.name) names.push(found.name);
        }
      });
      if (names.length) return names.join(" · ");
      return String(arr.length);
    },
    openFilterGroup(key) {
      // Same navigation model as the invoice specs sheet: close main, open sub.
      this.filterGroup = key;
      this.filterSheet = false;
      if (key === "custom") {
        this.loadFilterCustomFields();
        this.loadFilterCustomKeys();
      }
      this.$nextTick(() => {
        this.filterGroupSheet = true;
      });
    },
    loadFilterCustomFields() {
      const source = Array.isArray(this.invoiceList.customFields)
        ? this.invoiceList.customFields
        : [];
      this.filterCustomItems = source.map((item) => ({
        key: item && item.key != null ? item.key : null,
        value: item && item.value != null ? item.value : null,
      }));
    },
    syncFilterCustomFields() {
      // Same shape the desktop custom-fields component writes on the shell.
      this.invoiceList.customFields = this.filterCustomItems.map((item) => ({
        key: item.key,
        value: item.value,
      }));
    },
    addFilterCustomField() {
      this.filterCustomItems.push({ key: null, value: null });
      this.syncFilterCustomFields();
    },
    removeFilterCustomField(index) {
      this.filterCustomItems.splice(index, 1);
      this.syncFilterCustomFields();
    },
    loadFilterCustomKeys() {
      clearTimeout(this.filterCustomKeyTimer);
      this.filterCustomKeyTimer = setTimeout(() => {
        this.$axios
          .get(this.$endpoint + "getdetail", {
            params: {
              select: "invoiceCustomFields",
              search: this.filterCustomSearch,
            },
          })
          .then((res) => {
            this.filterCustomKeyItems = res.data || [];
          })
          .catch((err) => {
            this.invoiceList.serverErrorHandeler(err);
          });
      }, 400);
    },
    backToFilterMain() {
      // Same reset as invoice create when its sheets close: reopening the
      // custom-fields layer starts with a clean key search and list.
      if (this.filterGroup === "custom") {
        this.filterCustomSearch = null;
        this.filterCustomItems = [];
        this.filterCustomKeyItems = [];
      }
      this.filterGroupSheet = false;
      this.$nextTick(() => {
        this.filterSheet = true;
      });
    },
    hasValue(v) {
      if (Array.isArray(v)) return v.length > 0;
      return v !== null && v !== undefined && v !== "";
    },
    groupHasValue(key) {
      const list = this.invoiceList;
      if (key === "customer") {
        return (
          this.hasValue(list.account) ||
          this.hasValue(list.currencyFilter) ||
          this.hasValue(list.registerar)
        );
      }
      if (key === "amountNumber") {
        return (
          this.hasValue(list.fromNumber) ||
          this.hasValue(list.toNumber) ||
          this.hasValue(list.fromAmount) ||
          this.hasValue(list.toAmount)
        );
      }
      if (key === "date") {
        return (
          this.hasValue(list.fromDate) ||
          this.hasValue(list.toDate) ||
          this.hasValue(list.fromDueDate) ||
          this.hasValue(list.toDueDate) ||
          this.hasValue(list.fromTime) ||
          this.hasValue(list.toTime)
        );
      }
      if (key === "category") {
        return (
          this.hasValue(list.tag) ||
          this.hasValue(list.category) ||
          this.hasValue(list.branch) ||
          this.hasValue(list.entrepot)
        );
      }
      if (key === "status") {
        return (
          this.hasValue(list.sellStatus) ||
          this.hasValue(list.lockStatus) ||
          this.hasValue(list.payedStatus)
        );
      }
      if (key === "custom") return this.hasValue(list.customFields);
      return false;
    },
    groupSubtitle(key) {
      const list = this.invoiceList;
      if (key === "customer") {
        return (
          this.$t("customer") +
          " · " +
          this.$t("currency") +
          " · " +
          this.$t("registerar")
        );
      }
      if (key === "amountNumber") {
        const parts = [];
        if (this.hasValue(list.fromNumber)) {
          parts.push(this.$t("from") + " " + this.$t("number") + ": " + list.fromNumber);
        }
        if (this.hasValue(list.toNumber)) {
          parts.push(this.$t("until") + " " + this.$t("number") + ": " + list.toNumber);
        }
        if (this.hasValue(list.fromAmount)) {
          parts.push(this.$t("from") + " " + this.$t("amount") + ": " + list.fromAmount);
        }
        if (this.hasValue(list.toAmount)) {
          parts.push(this.$t("until") + " " + this.$t("amount") + ": " + list.toAmount);
        }
        return parts.length
          ? parts.join(" · ")
          : this.$t("amount") + " / " + this.$t("number");
      }
      if (key === "date") {
        const parts = [];
        if (this.hasValue(list.fromDate)) parts.push(list.fromDate);
        if (this.hasValue(list.toDate)) parts.push(list.toDate);
        if (this.hasValue(list.fromDueDate)) parts.push(list.fromDueDate);
        if (this.hasValue(list.toDueDate)) parts.push(list.toDueDate);
        return parts.length
          ? parts.join(" · ")
          : this.$t("date") + " · " + this.$t("dueDate");
      }
      if (key === "category") {
        return (
          this.$t("tag") +
          " · " +
          this.$t("category") +
          " · " +
          this.$t("branch") +
          " · " +
          this.$t("entrepot")
        );
      }
      if (key === "status") {
        const parts = [];
        const lockItem = (list.lockStatusItems || []).find(
          (i) => i.value === list.lockStatus
        );
        if (lockItem) parts.push(lockItem.text);
        const payedItem = (list.payedStatuses || []).find(
          (i) => i.value === list.payedStatus
        );
        if (payedItem) parts.push(payedItem.label);
        return parts.length ? parts.join(" · ") : this.$t("status");
      }
      if (key === "custom") {
        return this.$t("invoice-mobile-custom-field-extras-subtitle");
      }
      return "";
    },
    numpadFieldText(key) {
      const value = this.invoiceList[key];
      if (!this.hasValue(value)) return "—";
      // Only amounts get thousand separators; invoice numbers stay raw.
      const field = this.numpadFields.find((f) => f.key === key);
      if (field && field.isAmount && this.$numberFormat) {
        return this.$numberFormat(value, "plain", 0);
      }
      return String(value);
    },
    openNumpad(field) {
      this.numpadField = field.key;
      this.numpadLabel = field.label;
      this.numpadIsAmount = !!field.isAmount;
      const current = this.invoiceList[field.key];
      this.numpadDraft = this.hasValue(current) ? String(current) : "";
      this.numpadOpen = true;
    },
    numpadDigit(num) {
      this.numpadAppend(String(num));
    },
    numpadAppend(chunk) {
      const piece = String(chunk);
      if (!piece) return;
      if (String(this.numpadDraft).length + piece.length > 16) return;
      if (!this.numpadDraft && /^\d+$/.test(piece)) {
        this.numpadDraft = piece.replace(/^0+(?=\d)/, "");
      } else if (this.numpadDraft === "0" && /^\d+$/.test(piece)) {
        this.numpadDraft = piece;
      } else {
        this.numpadDraft = String(this.numpadDraft) + piece;
      }
    },
    numpadDot() {
      // No decimals for invoice numbers.
      if (!this.numpadIsAmount) return;
      if (!this.numpadDraft) {
        this.numpadDraft = "0.";
        return;
      }
      if (String(this.numpadDraft).includes(".")) return;
      this.numpadDraft = String(this.numpadDraft) + ".";
    },
    numpadBackspace() {
      const current = String(this.numpadDraft || "");
      this.numpadDraft = current.length <= 1 ? "" : current.slice(0, -1);
    },
    numpadClear() {
      this.numpadDraft = "";
    },
    numpadStep(delta) {
      const next = (parseFloat(this.numpadDraft) || 0) + delta;
      this.numpadDraft = next < 0 ? "0" : String(next);
    },
    openDateLayer(field) {
      this.dateLayerField = field.key;
      this.dateLayerLabel = field.label;
      this.dateLayerIsTime = !!field.isTime;
      this.dateLayerOpen = true;
    },
    confirmNumpad() {
      // Writes the same shell filter field the desktop money/text inputs write.
      if (this.numpadField) {
        const raw = String(this.numpadDraft || "").trim();
        this.invoiceList[this.numpadField] =
          raw === "" ? null : parseFloat(raw);
      }
      this.numpadField = null;
      this.numpadOpen = false;
    },
    applyFilter() {
      this.filterSheet = false;
      // Reload from the first page, same as the search reset; getItems is the
      // same call the desktop filter button makes.
      this.appending = false;
      this.invoiceList.options = Object.assign({}, this.invoiceList.options, {
        page: 1,
      });
      this.invoiceList.getItems();
    },
    clearFilter() {
      // Reset every filter field to its shell data default, then re-run.
      const list = this.invoiceList;
      list.account = null;
      list.sellStatus = null;
      list.currencyFilter = null;
      list.fromAmount = null;
      list.toAmount = null;
      list.fromNumber = null;
      list.toNumber = null;
      list.fromDate = null;
      list.toDate = null;
      list.fromDueDate = null;
      list.toDueDate = null;
      list.fromTime = null;
      list.toTime = null;
      list.tag = null;
      list.category = null;
      list.branch = null;
      list.entrepot = null;
      list.lockStatus = null;
      list.registerar = null;
      list.payedStatus = null;
      list.customFields = null;
      this.applyFilter();
    },
    askDelete(item, type) {
      // Mirrors shell openDeleteDialog (same deleteItem/removeType state),
      // but opens the mobile confirmation sheet instead of the desktop dialog.
      this.deleteGroupMode = false;
      this.invoiceList.deleteItem = item;
      this.invoiceList.removeType = type;
      this.deleteSheet = true;
    },
    confirmDelete() {
      this.deleteSheet = false;
      // Same branches as the desktop confirmation dialog buttons.
      if (this.invoiceList.selected && this.invoiceList.selected.length > 0) {
        this.invoiceList.removegroup();
      } else {
        this.invoiceList.remove();
      }
      if (this.deleteGroupMode) {
        this.deleteGroupMode = false;
        this.exitSelectionMode();
      }
    },
    openDetails(item) {
      this.detailItem = item;
      this.detailSheet = true;
    },
    runMoreAction(name) {
      // Close the "more" layer first; runNamedAction closes the details
      // sheet itself and runs the same shell method as before.
      this.moreSheet = false;
      this.runNamedAction(name);
    },
    onCopyLink() {
      // Same URL as desktop row "copy-link"; clipboard is on the button.
      this.moreSheet = false;
      this.detailSheet = false;
      if (typeof this.copied === "function") this.copied();
    },
    systemStatusLabel(item) {
      // Mirror desktop item.system_status chip text.
      const tp = item && item.tax_payer;
      if (!tp) return "—";
      if (tp.status == "error") return this.$t("error-in-sending-invoice");
      if (tp.status == "sent") return this.$t("shipped");
      if (tp.status == "done") return this.$t("confirmed");
      if (tp.status == "editSent") return this.$t("edited");
      if (tp.status == "removeSent") return this.$t("cenceled");
      return this.invoiceList.resolveTaxPayerStatus(tp) || "—";
    },
    systemStatusColor(item) {
      const tp = item && item.tax_payer;
      if (!tp) return "secondary";
      if (tp.status == "error") return "error";
      if (tp.status == "sent") return "primary";
      if (tp.status == "done") return "success";
      if (tp.status == "editSent") return "warning";
      if (tp.status == "removeSent") return "error";
      return this.invoiceList.resolveTaxPayerStatusColor(tp) || "secondary";
    },
    systemStatusClickable(item) {
      // Desktop only opens the dialog for non-terminal chip statuses.
      const tp = item && item.tax_payer;
      if (!tp) return false;
      return (
        tp.status != "error" &&
        tp.status != "sent" &&
        tp.status != "done" &&
        tp.status != "editSent" &&
        tp.status != "removeSent"
      );
    },
    onSystemStatusClick() {
      if (!this.systemStatusClickable(this.detailItem)) return;
      this.invoiceList.openTaxPayerStatus(this.detailItem.tax_payer);
    },
    runNamedAction(name) {
      const item = this.detailItem;
      const list = this.invoiceList;
      this.detailSheet = false;
      if (!item) return;
      // Same methods and conditions as the desktop row action menu.
      if (name === "lock") list.lock(item, item.lock ? "unlock" : "lock");
      else if (name === "editCategory") list.editCategory(item);
      else if (name === "print") list.printChoice(item);
      else if (name === "view") list.viewInvoice(item);
      else if (name === "selectedPrint") list.selectedPrint(item);
      else if (name === "fishPrint") list.fishPrint(item);
      else if (name === "documents") list.openDocumentsDialog(item);
      else if (name === "invoiceSms") list.invoiceSms(item);
      else if (name === "entrepotPrint") list.entrepotPrint(item);
      else if (name === "barcode") list.barcodeSingle(item);
      else if (name === "taxSend") list.taxPayerConfirmationVMenu(item);
      else if (name === "copy") list.copyInvoice(item);
      else if (name === "editTax") list.editTaxInvoice(item);
      else if (name === "annulment") this.askDelete(item, "annulment");
    },
    detailRows(item) {
      if (!item) return [];
      const rows = [];
      // kind only drives row/value coloring in the details sheet
      // (money band, final amount, remain due/paid) — not the structure.
      const push = (label, value, kind) =>
        rows.push({
          label,
          value: value == null || value === "" ? "—" : value,
          kind: kind || null,
        });
      push(
        this.$t("detailsName"),
        item.customer ? item.customer.name : null,
        "customer"
      );
      push(
        this.$t("mobile"),
        item.customer ? item.customer.mobile : null
      );
      push(this.$t("number"), item.number);
      if (item.official_number) push(this.$t("number"), item.official_number);
      push(
        this.$t("document-number"),
        item.doc_header ? item.doc_header.number : null
      );
      push(this.$t("id"), item.id);
      push(this.$t("date"), item.date);
      push(this.$t("dueDate"), item.due_date);
      push(
        this.$t("discount"),
        this.formatAmount(item, item.discount || 0),
        "money"
      );
      push(this.$t("tax"), this.formatAmount(item, item.tax || 0), "money");
      push(
        this.$t("finalAmount"),
        this.formatAmount(item, item.price ? item.price.price : 0),
        "final"
      );
      push(
        this.$t("payedPrice"),
        item.payed ? this.formatAmount(item, item.payed.amount || 0) : null,
        "money"
      );
      push(
        this.$t("remainPrice"),
        this.remainText(item),
        !item.payed ? "money" : this.isRemain(item) ? "remainDue" : "remainPaid"
      );
      push(
        this.$t("entrepot"),
        item.entrepot
          ? this.$t(item.entrepot.type) +
              (item.entrepot.entrepot && item.entrepot.entrepot.name
                ? " " + item.entrepot.entrepot.name
                : "")
          : null
      );
      push(this.$t("branch"), item.branch ? item.branch.name : null);
      push(
        this.$t("status"),
        item.last_status && item.last_status.status
          ? item.last_status.status.name
          : null
      );
      push(this.$t("registerar"), item.user ? item.user.name : null);
      push(this.$t("other"), item.content);
      return rows;
    },
    integratorDetailRows(item) {
      if (
        !item ||
        !this.invoiceList ||
        typeof this.invoiceList.invoiceListOtherDetailRows !== "function"
      ) {
        return [];
      }
      return this.invoiceList.invoiceListOtherDetailRows(item);
    },
    channelLabel(channel) {
      if (!channel) return "";
      const key = String(channel);
      const translated = this.$t(key);
      return translated === key ? key : translated;
    },
    onCopyIntegratorId() {
      if (typeof this.copied === "function") this.copied();
      else if (
        this.invoiceList &&
        typeof this.invoiceList.copied === "function"
      ) {
        this.invoiceList.copied();
      }
    },
    openIntegratorOrder(row) {
      this.detailSheet = false;
      if (
        this.invoiceList &&
        typeof this.invoiceList.openIntegratorChannel === "function"
      ) {
        this.invoiceList.openIntegratorChannel(row);
      }
    },
    toggleSelectionMode() {
      // Selection is kept locally while the mode is on: the shell clears
      // `selected` on every getItems, which would wipe earlier pages'
      // picks during infinite scroll. It is synced into
      // invoiceList.selected only when a group action runs, so every
      // shell group method (and its watchers) behaves exactly like desktop.
      this.selectionMode = !this.selectionMode;
      if (!this.selectionMode) {
        this.selectedItems = [];
        this.bulkMoreSheet = false;
        this.invoiceList.selected = [];
      }
    },
    exitSelectionMode() {
      this.selectionMode = false;
      this.selectedItems = [];
      this.bulkMoreSheet = false;
    },
    isSelected(item) {
      return this.selectedItems.some((i) => i.id === item.id);
    },
    toggleSelect(item) {
      if (this.isSelected(item)) {
        this.selectedItems = this.selectedItems.filter(
          (i) => i.id !== item.id
        );
      } else {
        this.selectedItems = this.selectedItems.concat([item]);
      }
    },
    selectAllLoaded() {
      const seen = {};
      this.selectedItems.forEach((i) => {
        seen[i.id] = true;
      });
      this.selectedItems = this.selectedItems.concat(
        this.displayItems.filter((i) => !seen[i.id])
      );
    },
    clearSelection() {
      this.selectedItems = [];
    },
    syncSelection() {
      this.invoiceList.selected = this.selectedItems.slice();
    },
    bulkLock(type) {
      if (!this.selectedItems.length) return;
      this.syncSelection();
      if (type === "lock") this.invoiceList.lockGroup("lock");
      else this.invoiceList.unlockGroup("unlock");
      this.exitSelectionMode();
    },
    bulkGroupPrint() {
      if (!this.selectedItems.length) return;
      this.syncSelection();
      this.invoiceList.groupPrintInvoices();
    },
    bulkCheckAverage() {
      if (!this.selectedItems.length) return;
      this.bulkMoreSheet = false;
      this.syncSelection();
      // selectedCheck is derived by the shell `selected` watcher — call
      // after it flushes, exactly like desktop where selection predates the click.
      this.$nextTick(() => this.invoiceList.fetchCheckAverageFirst());
    },
    bulkProductBarcode() {
      // Same shell barcode() as desktop toolbar productBarcode (uses selected).
      if (!this.selectedItems.length) return;
      this.bulkMoreSheet = false;
      this.syncSelection();
      this.$nextTick(() => this.invoiceList.barcode());
    },
    bulkReviewDigikala() {
      // Same shell reviewDigikalaOrder as desktop toolbar (uses selected).
      if (!this.selectedItems.length) return;
      this.bulkMoreSheet = false;
      this.syncSelection();
      this.$nextTick(() => this.invoiceList.reviewDigikalaOrder());
    },
    bulkTaxPayer() {
      if (!this.selectedItems.length) return;
      this.bulkMoreSheet = false;
      this.syncSelection();
      // taxPayerSelectedItems also comes from the shell `selected` watcher.
      this.$nextTick(() => this.invoiceList.taxPayerConfirmation());
    },
    selectedRowNumber(item) {
      // 1-based position of the item in the currently loaded list,
      // so the confirm list shows which rows were picked (e.g. 1, 3, 10).
      const idx = this.displayItems.findIndex((i) => i.id === item.id);
      return idx >= 0 ? idx + 1 : "";
    },
    askGroupDelete() {
      if (!this.selectedItems.length) return;
      // Same mobile confirmation sheet as single delete; confirmDelete
      // branches to removegroup() because the shell selection is set.
      this.syncSelection();
      this.invoiceList.deleteItem = null;
      this.invoiceList.removeType = "delete";
      this.deleteGroupMode = true;
      this.deleteSheet = true;
    },
  },
};
</script>

<style scoped>
.ilm {
  display: flex;
  flex-direction: column;
  padding: 0 12px 12px;
  background: #ffffff;
  min-height: calc(100vh - 52px);
  box-sizing: border-box;
  padding-bottom: calc(16px + env(safe-area-inset-bottom, 0px));
}
.ilm__header {
  position: sticky;
  top: 52px;
  z-index: 4;
  background: #ffffff;
  padding: 6px 0 10px;
  border-bottom: 1px solid #eef1f4;
  margin-bottom: 10px;
}
.ilm__bar {
  display: flex;
  align-items: center;
  gap: 2px;
  margin-bottom: 8px;
}
.ilm__bar-actions {
  display: flex;
  align-items: center;
  gap: 2px;
  flex: 0 0 auto;
}
.ilm__bar-btn {
  width: 38px;
  height: 38px;
  border: 0;
  border-radius: 10px;
  background: transparent;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
  -webkit-tap-highlight-color: transparent;
}
.ilm__bar-btn:active {
  background: rgba(91, 124, 153, 0.1);
}
.ilm__bar-btn--create {
  background: rgba(91, 124, 153, 0.14);
  border: 1px solid rgba(91, 124, 153, 0.28);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
}
.ilm__bar-btn--create:active {
  background: rgba(91, 124, 153, 0.24);
}
.ilm__bar-btn--back {
  flex: 0 0 auto;
}
.ilm__title {
  flex: 1 1 auto;
  text-align: center;
  font-size: 16px;
  font-weight: 700;
  color: #263238;
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.ilm__search-row {
  display: flex;
  align-items: stretch;
  gap: 8px;
}
.ilm__search {
  flex: 1 1 auto;
  min-width: 0;
  height: 44px;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 12px;
  border-radius: 10px;
  background: #f3f5f7;
  border: 1px solid #e0e4e8;
  box-sizing: border-box;
}
.ilm__search-icon {
  flex: 0 0 auto;
}
.ilm__search-input {
  flex: 1 1 auto;
  min-width: 0;
  height: 100%;
  border: 0;
  outline: none;
  background: transparent;
  font-size: 14px;
  font-weight: 600;
  color: #263238;
  text-align: right;
}
.ilm__search-input::placeholder {
  color: #90a4ae;
  font-weight: 500;
}
.ilm__search-input::-webkit-search-cancel-button {
  display: none;
}
.ilm__search-clear {
  flex: 0 0 auto;
  width: 28px;
  height: 28px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 0;
  border-radius: 10px;
  background: transparent;
  cursor: pointer;
  padding: 0;
}
.ilm__filter-btn {
  flex: 0 0 44px;
  height: 44px;
  border: 1px solid #e0e4e8;
  border-radius: 10px;
  background: #ffffff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
  -webkit-tap-highlight-color: transparent;
}
.ilm__filter-btn:active {
  background: rgba(91, 124, 153, 0.1);
}
/* Selection toggle active state, same language as the cashier-mode
   toggle in the invoice form header (soft blue bg + blue icon) */
.ilm__filter-btn--active {
  background: rgba(21, 101, 192, 0.1);
  border-color: rgba(21, 101, 192, 0.35);
}
.ilm__select-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-top: 8px;
  padding: 6px 10px;
  border: 1px solid #e8edf2;
  border-radius: 10px;
  background: #f8fafc;
}
.ilm__select-bar-btn {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 6px;
  border: 0;
  border-radius: 10px;
  background: transparent;
  font-size: 12px;
  font-weight: 600;
  color: #37474f;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}
.ilm__select-bar-btn:active {
  background: rgba(91, 124, 153, 0.12);
}
.ilm__select-count {
  font-size: 12px;
  font-weight: 700;
  color: #5b7c99;
}
.ilm__state {
  display: flex;
  justify-content: center;
  padding: 40px 0;
  color: #607d8b;
  font-size: 13px;
}
.ilm__state--empty {
  flex: 1 1 auto;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  min-height: calc(100vh - 220px);
  padding: 32px 16px 24px;
  background: #ffffff;
}
.ilm__empty-logo {
  width: 72px;
  height: 72px;
  object-fit: contain;
  opacity: 0.85;
}
.ilm__empty-text {
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  color: #78909c;
  text-align: center;
}
.ilm__state--more {
  padding: 14px 0;
}
.ilm__list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.ilm__sentinel {
  height: 1px;
}
.ilm-card {
  position: relative;
  background: #f8fafc;
  border: 1px solid #e8edf2;
  border-radius: 10px;
  padding: 12px;
  transition: transform 0.15s ease;
}
.ilm-card--selected {
  transform: scale(0.99);
  border-color: rgba(21, 101, 192, 0.45);
}
.ilm-card__select-overlay {
  position: absolute;
  inset: 0;
  z-index: 3;
  border-radius: 10px;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}
.ilm-card__select-overlay--on {
  background: rgba(38, 50, 56, 0.12);
}
.ilm-card__check {
  position: absolute;
  top: 8px;
  left: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background: #0d47a1;
  box-shadow: 0 2px 6px rgba(13, 71, 161, 0.4);
}
.ilm--selecting .ilm__list {
  padding-bottom: 88px;
}
/* Fixed bulk action bar near the bottom edge (global footer is dashboard-only) */
.ilm-bulkbar {
  position: fixed;
  right: 12px;
  left: 12px;
  bottom: calc(16px + env(safe-area-inset-bottom, 0px));
  z-index: 6;
  display: flex;
  gap: 4px;
  padding: 8px;
  border: 1px solid #e8edf2;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.96);
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
  box-shadow: 0 8px 24px rgba(38, 50, 56, 0.14);
}
.ilm-bulkbar__btn {
  flex: 1 1 0;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 3px;
  min-height: 48px;
  padding: 6px 2px;
  border: 0;
  border-radius: 10px;
  background: rgba(91, 124, 153, 0.07);
  font-size: 10px;
  font-weight: 600;
  line-height: 1.25;
  text-align: center;
  color: #37474f;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}
.ilm-bulkbar__btn:active {
  background: rgba(91, 124, 153, 0.15);
}
.ilm-bulkbar__btn:disabled {
  opacity: 0.4;
  cursor: default;
}
.ilm-bulkbar__btn--danger {
  color: #e53935;
  background: rgba(229, 57, 53, 0.06);
}
.ilm-card__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}
.ilm-card__customer {
  font-size: 14px;
  font-weight: 700;
  color: #263238;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.ilm-card__customer-wrap {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}
/* Customer name as an obvious link to the account component (desktop parity) */
.ilm-card__customer--link {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  min-width: 0;
  padding: 2px 4px;
  margin: -2px -4px;
  border: 0;
  border-radius: 10px;
  background: transparent;
  color: #5b7c99;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}
.ilm-card__customer--link:active {
  background: rgba(91, 124, 153, 0.12);
}
.ilm-card__customer-name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
/* Customer block: flat tinted surface, separated from the table below */
.ilm-sheet__customer-block {
  flex: 0 0 auto;
  background: #edf2f7;
  border-radius: 10px;
  padding: 10px 14px;
  margin: 6px 16px 0;
}
.ilm-sheet__customer-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}
.ilm-sheet__customer-name {
  font-size: 14px;
  font-weight: 800;
  color: #263238;
  text-align: left;
}
.ilm-sheet__customer-block .ilm-sheet__linkrow {
  margin: 8px 0 0;
  background: rgba(255, 255, 255, 0.75);
}
.ilm-sheet__customer-block .ilm-sheet__linkrow:active {
  background: rgba(255, 255, 255, 0.95);
}
/* Payment details layer: summary block + read-only payment cards */
.ilm-pay-summary {
  flex: 0 0 auto;
  background: #f8fafc;
  border: 1px solid #e8edf2;
  border-radius: 10px;
  padding: 4px 14px;
  margin: 6px 16px 0;
}
.ilm-pay-summary .ilm-sheet__row:last-child {
  border-bottom: 0;
}
.ilm-pay-empty {
  padding: 18px 0;
  font-size: 12.5px;
  color: #90a4ae;
  text-align: center;
}
.ilm-pay-card {
  background: #ffffff;
  border: 1px solid #e8edf2;
  border-radius: 10px;
  padding: 10px 14px 4px;
  margin-bottom: 10px;
}
.ilm-pay-card .ilm-sheet__row:last-child {
  border-bottom: 0;
}
.ilm-pay-card__head {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 4px;
}
.ilm-pay-card__index {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 22px;
  height: 22px;
  padding: 0 6px;
  border-radius: 10px;
  background: rgba(91, 124, 153, 0.12);
  font-size: 11px;
  font-weight: 700;
  color: #5b7c99;
}
.ilm-pay-card__type {
  font-size: 13px;
  font-weight: 700;
  color: #263238;
}
/* Turnover as a full-width navigation row in the customer block */
.ilm-sheet__linkrow {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  height: 40px;
  margin: 6px 0 2px;
  padding: 0 10px;
  border: 1px solid rgba(0, 150, 136, 0.16);
  border-radius: 10px;
  background: rgba(0, 150, 136, 0.07);
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}
.ilm-sheet__linkrow:active {
  background: rgba(0, 150, 136, 0.15);
}
.ilm-sheet__linkrow-text {
  flex: 1 1 auto;
  font-size: 12.5px;
  font-weight: 700;
  color: #00796b;
  text-align: right;
}
.ilm-sheet__integrator {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 4px 0;
  border-top: 1px solid rgba(91, 124, 153, 0.12);
  margin-top: 4px;
}
.ilm-sheet__integrator-block {
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.ilm-sheet__row--id {
  align-items: center;
}
.ilm-sheet__id-label {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.ilm-sheet__id-icon {
  width: 18px;
  height: 18px;
  border-radius: 0;
  object-fit: contain;
}
.ilm-sheet__id-value {
  border: 0;
  background: transparent;
  padding: 0;
  cursor: pointer;
  color: #78909c;
  text-align: left;
  font: inherit;
  direction: ltr;
}
.ilm-card__number {
  flex: 0 0 auto;
  font-size: 12px;
  font-weight: 600;
  color: #5b7c99;
  background: rgba(91, 124, 153, 0.1);
  border-radius: 10px;
  padding: 2px 8px;
}
.ilm-card__meta {
  display: flex;
  align-items: center;
  margin-top: 4px;
  font-size: 12px;
  color: #90a4ae;
}
.ilm-card__amounts {
  margin-top: 10px;
  border-top: 1px dashed rgba(144, 164, 174, 0.35);
  padding-top: 8px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.ilm-card__amount-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.ilm-card__amount-label {
  font-size: 12px;
  color: #78909c;
}
.ilm-card__amount-value {
  font-size: 13px;
  font-weight: 600;
  color: #37474f;
}
.ilm-card__amount-value--remain {
  color: #e53935;
}
.ilm-card__amount-value--paid {
  color: #43a047;
}
.ilm-card__footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-top: 10px;
}
.ilm-card__actions {
  display: flex;
  gap: 6px;
}
.ilm-btn {
  flex: 0 0 auto;
  height: 28px;
  padding: 0 10px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}
.ilm-btn .v-icon {
  margin-inline-end: 2px;
}
.ilm-btn--primary {
  border: 0;
  background: #5b7c99;
  color: #ffffff;
}
.ilm-btn--primary:disabled {
  opacity: 0.45;
  cursor: default;
}
.ilm-btn--primary:active {
  background: #4d6a84;
}
.ilm-btn--danger {
  border: 1px solid rgba(229, 57, 53, 0.35);
  background: rgba(229, 57, 53, 0.06);
  color: #e53935;
}
.ilm-btn--danger:disabled {
  opacity: 0.45;
  cursor: default;
}
.ilm-btn--outline {
  border: 1px solid #cfd8dc;
  background: #ffffff;
  color: #5b7c99;
}
.ilm-pay-btn {
  flex: 0 0 auto;
  height: 28px;
  padding: 0 8px;
  border: 0;
  border-radius: 10px;
  background: rgba(91, 124, 153, 0.08);
  color: #5b7c99;
  font-size: 11px;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}
.ilm-pay-btn:disabled {
  background: transparent;
  color: #90a4ae;
  opacity: 0.55;
  cursor: default;
}
.ilm-sheet {
  border-radius: 10px 10px 0 0 !important;
  max-height: 88vh;
  display: flex;
  flex-direction: column;
}
.ilm-sheet__handle {
  flex: 0 0 auto;
  width: 44px;
  height: 4px;
  border-radius: 10px;
  background: #cfd8dc;
  margin: 10px auto 2px;
}
.ilm-sheet__header {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px 10px;
  border-bottom: 1px solid #eef1f4;
}
.ilm-sheet__header-spacer {
  flex: 0 0 40px;
  width: 40px;
}
.ilm-sheet__header-side {
  flex: 1 1 0;
  display: flex;
  align-items: center;
  min-width: 0;
}
.ilm-sheet__header-side--end {
  justify-content: flex-end;
}
.ilm-sheet__num-chip {
  display: inline-flex;
  align-items: center;
  height: 26px;
  padding: 0 10px;
  border-radius: 10px;
  background: rgba(135, 158, 182, 0.14);
  border: 1px solid rgba(135, 158, 182, 0.28);
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
  color: #5b7c99;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
}
.ilm-sheet__title {
  flex: 1 1 auto;
  font-size: 15px;
  font-weight: 700;
  color: #263238;
  text-align: center;
}
.ilm-sheet__back {
  flex: 0 0 auto;
  width: 40px;
  height: 40px;
  border: 0;
  border-radius: 10px;
  background: transparent;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
}
.ilm-sheet__back:active {
  background: rgba(91, 124, 153, 0.1);
}
.ilm-sheet__quickbar {
  flex: 0 0 auto;
  display: flex;
  gap: 6px;
  padding: 8px 12px 0;
}
.ilm-sheet__quick {
  flex: 1 1 0;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
  height: 46px;
  padding: 4px 2px;
  border: 1px solid #e8edf2;
  border-radius: 10px;
  background: rgba(91, 124, 153, 0.07);
  font-size: 9px;
  font-weight: 600;
  line-height: 1.25;
  text-align: center;
  color: #37474f;
  cursor: pointer;
  overflow: hidden;
  -webkit-tap-highlight-color: transparent;
}
.ilm-sheet__quick .v-icon {
  font-size: 18px !important;
  width: 18px;
  height: 18px;
  flex: 0 0 auto;
}
.ilm-sheet__quick:active {
  background: rgba(91, 124, 153, 0.14);
}
.ilm-sheet__quick:disabled {
  opacity: 0.45;
  cursor: default;
}
.ilm-sheet__body {
  overflow-y: auto;
  padding: 8px 16px 20px;
}
/* Same soft card surface as the invoice list cards (#F8FAFC / #E8EDF2) */
.ilm-sheet__card {
  background: #f8fafc;
  border: 1px solid #e8edf2;
  border-radius: 10px;
  padding: 4px 14px;
}
.ilm-sheet__row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 8px 0;
  border-bottom: 1px solid #e8edf2;
}
.ilm-sheet__card .ilm-sheet__row:last-child {
  border-bottom: 0;
}
.ilm-sheet__row-label {
  flex: 0 0 auto;
  font-size: 12px;
  color: #90a4ae;
}
.ilm-sheet__row-value {
  font-size: 13px;
  font-weight: 600;
  color: #263238;
  text-align: left;
}
/* Coloring only (flat table unchanged): the money rows form one soft
   tinted band inside the card; final/remain values carry the semantic
   colors also used on the list cards. */
.ilm-sheet__row--band {
  margin: 0 -14px;
  padding: 8px 14px;
  border-bottom-color: rgba(91, 124, 153, 0.12);
  background: rgba(91, 124, 153, 0.05);
}
.ilm-sheet__row-value--money {
  color: #5b7c99;
  font-weight: 700;
}
.ilm-sheet__row-value--final {
  font-size: 14px;
  font-weight: 800;
  color: #5b7c99;
}
.ilm-sheet__row-value--due {
  color: #d32f2f;
}
.ilm-sheet__row-value--paid {
  color: #2e7d32;
}
.ilm-sheet__tags {
  display: inline-flex;
  flex-wrap: wrap;
  gap: 4px;
}
.ilm-sheet__tags .v-chip {
  border-radius: 10px !important;
}
.ilm-sheet__status-chip--click {
  cursor: pointer;
}
.ilm-sheet__actions {
  margin-top: 14px;
  display: flex;
  flex-direction: column;
}
.ilm-sheet__actions--sheet {
  margin-top: 0;
}
.ilm-sheet__action {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  height: 42px;
  padding: 0 6px;
  border: 0;
  border-bottom: 1px solid #e8edf2;
  border-radius: 10px;
  background: transparent;
  font-size: 13px;
  font-weight: 600;
  color: #37474f;
  cursor: pointer;
  text-align: right;
  -webkit-tap-highlight-color: transparent;
}
.ilm-sheet__action:active {
  background: rgba(91, 124, 153, 0.08);
}
.ilm-sheet__action:disabled {
  opacity: 0.45;
  cursor: default;
}
.ilm-sheet__action--danger {
  color: #e53935;
}
.ilm-sheet__action--log {
  cursor: default;
}
/* The history (<log>) list button, normalized to match the other action
   rows in the "more" layer: same height, icon size/color and title. */
.ilm-sheet__action--log > div {
  display: flex;
  flex: 1 1 auto;
  min-width: 0;
}
.ilm-sheet__action--log ::v-deep .v-list-item {
  flex: 1 1 auto;
  min-height: 42px;
  height: 42px;
  padding: 0;
}
.ilm-sheet__action--log ::v-deep .v-list-item__icon {
  margin: 0 !important;
  min-width: 19px;
  align-self: center;
}
.ilm-sheet__action--log ::v-deep .v-list-item__icon .v-icon {
  font-size: 19px !important;
  color: #5b7c99 !important;
}
.ilm-sheet__action--log ::v-deep .v-list-item__title {
  margin-inline-start: 10px;
  font-size: 13px;
  font-weight: 600;
  color: #37474f;
  text-align: right;
}
.ilm-sheet--filter {
  max-height: 92vh;
  border-radius: 20px 20px 0 0 !important;
}
.ilm-sheet__footer {
  flex: 0 0 auto;
  display: flex;
  gap: 8px;
  padding: 10px 12px calc(10px + env(safe-area-inset-bottom, 0px));
  border-top: 1px solid #eef1f4;
  background: #ffffff;
}
.ilm-sheet__btn {
  flex: 1 1 0;
  height: 44px;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}
.ilm-sheet__btn--primary {
  border: 0;
  background: #5b7c99;
  color: #ffffff;
}
.ilm-sheet__btn--primary:active {
  background: #4d6a84;
}
.ilm-sheet__btn--ghost {
  border: 1px solid #cfd8dc;
  background: #ffffff;
  color: #546e7a;
}
.ilm-sheet__btn--ghost:active {
  background: rgba(91, 124, 153, 0.08);
}
/* Same confirm-sheet model as Composite.vue (invoice create), plus one icon */
.ilm-confirm-sheet {
  border-radius: 20px 20px 0 0 !important;
  padding: 8px 16px 16px;
  background: #fff !important;
}
.ilm-confirm-sheet__handle {
  width: 40px;
  height: 4px;
  margin: 4px auto 14px;
  border-radius: 999px;
  background: #cfd8dc;
}
.ilm-confirm-sheet__icon {
  width: 52px;
  height: 52px;
  margin: 0 auto 10px;
  border-radius: 50%;
  background: rgba(229, 57, 53, 0.08);
  display: flex;
  align-items: center;
  justify-content: center;
}
.ilm-confirm-sheet__title {
  text-align: center;
  font-size: 18px;
  font-weight: 800;
  color: #212121;
  margin: 0 0 8px;
}
.ilm-confirm-sheet__subtitle {
  margin: 0 0 16px;
  text-align: center;
  font-size: 13px;
  font-weight: 600;
  color: #607d8b;
  line-height: 1.6;
}
.ilm-confirm-sheet__rows {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 18px;
}
.ilm-confirm-sheet__items {
  max-height: 168px;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  border: 1px solid #e8edf2;
  border-radius: 10px;
  background: #f8fafc;
  padding: 4px 12px;
}
.ilm-confirm-sheet__item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 7px 0;
  border-bottom: 1px solid #e8edf2;
}
.ilm-confirm-sheet__item-main {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  min-width: 0;
}
.ilm-confirm-sheet__item-index {
  flex: 0 0 auto;
  min-width: 16px;
  font-size: 11px;
  font-weight: 700;
  color: #90a4ae;
  text-align: center;
}
.ilm-confirm-sheet__item:last-child {
  border-bottom: 0;
}
.ilm-confirm-sheet__item-name {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 12.5px;
  font-weight: 600;
  color: #37474f;
}
.ilm-confirm-sheet__item-number {
  flex: 0 0 auto;
  font-size: 11.5px;
  font-weight: 700;
  color: #5b7c99;
}
.ilm-confirm-sheet__row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  min-height: 48px;
  padding: 12px 14px;
  border-radius: 12px;
  background: #f5f7f9;
  font-size: 13px;
  font-weight: 600;
  color: #607d8b;
}
.ilm-confirm-sheet__row strong {
  font-size: 14px;
  font-weight: 800;
  color: #263238;
  text-align: left;
}
.ilm-confirm-sheet__actions {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.ilm-confirm-sheet__yes {
  border-radius: 12px !important;
  font-weight: 800 !important;
  letter-spacing: 0 !important;
}
.ilm-confirm-sheet__no {
  border-radius: 12px !important;
  font-weight: 700 !important;
  letter-spacing: 0 !important;
}
/* Filter layers: same nav/numpad model as the invoice specs sheet
   (invoice-more-sheet__item / invoice-row-numpad in InvoiceMobile). */
.ilm-filter-nav {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding: 4px 0;
  background: #ffffff;
}
.ilm-filter-nav__item {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  width: 100%;
  gap: 12px;
  padding: 12px 16px;
  border: 0;
  border-bottom: 1px solid #eeeeee;
  background: #fff;
  text-align: right;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}
.ilm-filter-nav__item:active {
  background: #f7f9fc;
}
.ilm-filter-nav__item:last-child {
  border-bottom: 0;
}
.ilm-filter-nav__icon {
  flex: 0 0 auto;
}
.ilm-filter-nav__texts {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  flex: 1 1 auto;
  min-width: 0;
  text-align: right;
}
.ilm-filter-nav__item-title {
  display: block;
  width: 100%;
  font-size: 14px;
  font-weight: 700;
  color: #212121;
  line-height: 1.35;
  text-align: right;
}
.ilm-filter-nav__item-sub {
  display: block;
  width: 100%;
  margin-top: 2px;
  font-size: 12px;
  font-weight: 400;
  color: #90a4ae;
  line-height: 1.35;
  text-align: right;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.ilm-filter-nav__dot {
  flex: 0 0 auto;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #5b7c99;
}
.ilm-filter-fields {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding: 14px 16px 6px;
  background: #ffffff;
}
.ilm-picker-host {
  display: none !important;
}
.ilm-picker-overlay {
  position: relative;
  height: 40px;
  overflow: hidden;
  border-radius: 10px;
}
.ilm-picker-cover {
  position: absolute !important;
  inset: 0;
  z-index: 2;
  width: 100%;
  height: 100%;
}
.ilm-picker-cover__clear {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  flex: 0 0 auto;
}
.ilm-select-layer {
  padding: 6px 16px 20px;
}
.ilm-select-layer__item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  min-height: 48px;
  padding: 10px 4px;
  border-bottom: 1px solid #eeeeee;
  background: transparent;
  text-align: right;
  cursor: pointer;
}
.ilm-select-layer__text {
  font-size: 13.5px;
  font-weight: 500;
  color: #37474f;
}
.ilm-select-layer__clear {
  display: block;
  width: 100%;
  padding: 14px 4px 4px;
  background: transparent;
  font-size: 13px;
  font-weight: 600;
  color: #c62828;
  text-align: center;
  cursor: pointer;
}
.ilm-filter-fields > * {
  margin-bottom: 14px;
}
.ilm-field-tap {
  cursor: pointer;
}
/* Vuetify .v-input is flex:1 1 auto; inside sheets it must never shrink. */
.ilm-filter-fields ::v-deep .v-input {
  flex: 0 0 auto;
}
/* Filter fields follow the invoice-create field model
   (invoice-edit-field-wrap / invoice-edit-field in InvoiceMobile):
   label above, fixed-height bordered box, floating label hidden. */
.ilm-f-field-wrap {
  display: flex;
  flex-direction: column;
  width: 100%;
}
.ilm-f-field-wrap__label {
  display: block;
  flex: 0 0 auto;
  font-size: 13px;
  font-weight: 600;
  color: #455a64;
  text-align: right;
  line-height: 1.4;
  margin: 0 2px 4px;
}
/* Same root reset as .invoice-edit-field in InvoiceMobile: without it the
   Vuetify input keeps its own top margin/padding and the label above it
   floats too far from the box. */
.ilm-f-field.v-input,
.ilm-f-field ::v-deep .v-input {
  margin: 0 !important;
  padding: 0 !important;
  font-size: 14px;
}
/* The bordered box must start at the very top of the component (like the
   40px edit-field in invoice create); any internal top spacing of
   v-autocomplete/v-select detaches the wrap label from its box. */
.ilm-f-field ::v-deep .v-text-field,
.ilm-f-field.v-input.v-text-field {
  margin-top: 0 !important;
  padding-top: 0 !important;
}
.ilm-f-field ::v-deep .v-text-field__details,
.ilm-f-field ::v-deep .v-messages {
  display: none !important;
  min-height: 0 !important;
}
.ilm-f-field ::v-deep .v-input__control,
.ilm-f-field.v-input ::v-deep .v-input__control {
  min-height: 40px !important;
}
.ilm-f-field ::v-deep .v-input__slot,
.ilm-f-field.v-input ::v-deep .v-input__slot {
  min-height: 40px !important;
  margin: 0 !important;
  padding: 0 12px !important;
  border: 1px solid #cfd8dc !important;
  border-radius: 10px !important;
  background: #fff !important;
  box-shadow: none !important;
}
.ilm-f-field ::v-deep fieldset {
  display: none !important;
}
.ilm-f-field ::v-deep .v-input__slot::before,
.ilm-f-field ::v-deep .v-input__slot::after,
.ilm-f-field.v-input ::v-deep .v-input__slot::before,
.ilm-f-field.v-input ::v-deep .v-input__slot::after {
  display: none !important;
}
.ilm-f-field ::v-deep .v-input__append-inner,
.ilm-f-field ::v-deep .v-input__prepend-inner,
.ilm-f-field.v-input ::v-deep .v-input__append-inner,
.ilm-f-field.v-input ::v-deep .v-input__prepend-inner {
  margin-top: 0 !important;
  align-self: center !important;
}
.ilm-f-field ::v-deep .v-label,
.ilm-f-field.v-input ::v-deep .v-label {
  display: none !important;
}
.ilm-f-field ::v-deep input,
.ilm-f-field ::v-deep .v-select__selection,
.ilm-f-field.v-input ::v-deep input,
.ilm-f-field.v-input ::v-deep .v-select__selection {
  margin: 0 !important;
  font-size: 14px;
  color: #263238;
}
.ilm-f-field ::v-deep .v-icon,
.ilm-f-field.v-input ::v-deep .v-icon {
  color: #78909c !important;
  font-size: 20px !important;
}
/* Input-like picker row (opens numpad / date layer), same model as
   invoice-edit-picker-field in InvoiceMobile. */
.ilm-f-picker-field {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  height: 40px;
  min-height: 40px;
  margin: 0;
  padding: 0 12px;
  border: 1px solid #cfd8dc;
  border-radius: 10px;
  background: #fff;
  box-sizing: border-box;
  cursor: pointer;
  text-align: right;
  -webkit-tap-highlight-color: transparent;
}
.ilm-f-picker-field:active {
  background: #eef4f8;
  border-color: #90caf9;
}
.ilm-f-picker-field__text {
  flex: 1 1 auto;
  min-width: 0;
  font-size: 14px;
  color: #263238;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
/* Custom fields, same model as the invoice-create custom-fields sheet
   (invoice-notification-sheet / invoice-notif-card in InvoiceMobile). */
.ilm-cf__hint {
  margin-bottom: 12px;
  font-size: 12px;
  font-weight: 600;
  color: #78909c;
  line-height: 1.5;
}
.ilm-cf__add {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  width: 100%;
  min-height: 44px;
  margin-bottom: 12px;
  border: 1px solid #c5d0e0;
  border-radius: 10px;
  background: #e8edf5;
  color: #5b7c99;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}
.ilm-cf__add:active {
  background: #d9e1ec;
}
.ilm-cf__empty {
  padding: 16px;
  border: 1px dashed #cfd8dc;
  border-radius: 12px;
  text-align: center;
  font-size: 13px;
  font-weight: 600;
  color: #90a4ae;
}
.ilm-cf-card {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 12px;
  padding: 12px;
  border: 1px solid #e0e4e8;
  border-radius: 12px;
  background: #fff;
}
.ilm-cf-card__type {
  display: flex;
  align-items: flex-end;
  gap: 8px;
}
.ilm-cf-card__type-field {
  flex: 1 1 auto;
  min-width: 0;
  margin: 0;
}
.ilm-cf-card__remove {
  flex: 0 0 40px;
  width: 40px;
  height: 40px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #f8bbd0;
  border-radius: 10px;
  background: #fce4ec;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}
.ilm-cf-card__input {
  width: 100%;
  height: 40px;
  padding: 0 10px;
  border: 1px solid #cfd8dc;
  border-radius: 10px;
  outline: none;
  background: #fff;
  text-align: center;
  font-size: 15px;
  font-weight: 700;
  color: #37474f;
  box-sizing: border-box;
}
.ilm-cf-card__input--text {
  text-align: right;
  font-weight: 600;
}
/* Lock the combobox at exactly 40px like invoice create
   (.invoice-notif-card .invoice-edit-field), so the remove button
   (flex-end aligned) sits exactly opposite the field. */
.ilm-cf-card .ilm-f-field {
  flex: none !important;
  width: 100%;
  height: 40px !important;
  max-height: 40px !important;
  margin: 0 !important;
  overflow: hidden !important;
}
.ilm-cf-card .ilm-f-field.v-input {
  margin: 0 !important;
  min-height: 40px !important;
  height: 40px !important;
  max-height: 40px !important;
}
.ilm-cf-card .ilm-f-field ::v-deep .v-input__control,
.ilm-cf-card .ilm-f-field ::v-deep .v-input__slot {
  margin: 0 !important;
  min-height: 40px !important;
  height: 40px !important;
  max-height: 40px !important;
}
.ilm-cf-card .ilm-f-field-wrap__label {
  margin: 0 2px 4px;
}
.ilm-cf-card:last-child {
  margin-bottom: 0;
}
.ilm-cf-card .ilm-f-field ::v-deep .v-select__selections {
  flex-wrap: nowrap;
  overflow: hidden;
  min-height: 0 !important;
  padding: 0 !important;
}
.ilm-cf-card .ilm-f-field ::v-deep .v-select__selection {
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  margin: 0 !important;
}
/* Inline date layer, same model as invoice-mobile-date in InvoiceMobile. */
.ilm-mobile-date {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 14px 16px;
  background: #ffffff;
}
.ilm-mobile-date__selected {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 12px 14px;
  border: 1px solid #e0e6ed;
  border-radius: 12px;
  background: #f7f9fc;
}
.ilm-mobile-date__label {
  width: 100%;
  font-size: 12px;
  font-weight: 600;
  color: #78909c;
}
.ilm-mobile-date__value {
  font-size: 18px;
  font-weight: 800;
  color: #263238;
  letter-spacing: 0.02em;
}
.ilm-mobile-date__clear {
  border: 0;
  background: transparent;
  color: #e91e63;
  font-size: 13px;
  font-weight: 700;
  padding: 4px 0;
  cursor: pointer;
}
.ilm-mobile-date__picker {
  width: 100%;
}
.ilm-mobile-date__picker ::v-deep .vpd-main,
.ilm-mobile-date__picker ::v-deep .vpd-wrapper,
.ilm-mobile-date__picker ::v-deep .vpd-container,
.ilm-mobile-date__picker ::v-deep .vpd-content {
  width: 100% !important;
  max-width: 100% !important;
  box-shadow: none !important;
  border: 1px solid #e0e6ed !important;
  border-radius: 12px !important;
  background: #fff !important;
  overflow: hidden;
}
.ilm-mobile-date__picker ::v-deep .vpd-header,
.ilm-mobile-date__picker ::v-deep .vpd-addon-list {
  background: #c5d0e0 !important;
  color: #37474f !important;
}
.ilm-mobile-date__picker ::v-deep .vpd-header *,
.ilm-mobile-date__picker ::v-deep .vpd-addon-list * {
  color: #37474f !important;
}
.ilm-mobile-date__picker ::v-deep .vpd-day.selected span,
.ilm-mobile-date__picker ::v-deep .vpd-day.selected,
.ilm-mobile-date__picker ::v-deep .selected {
  background: #c5d0e0 !important;
  color: #37474f !important;
  box-shadow: none !important;
}
.ilm-mobile-date__picker ::v-deep .vpd-input-group,
.ilm-mobile-date__picker ::v-deep input {
  display: none !important;
}
.ilm-numpad {
  padding: 6px 12px calc(12px + env(safe-area-inset-bottom, 0px));
  border-radius: 16px 16px 0 0 !important;
  background: #f3f5f7 !important;
}
.ilm-numpad__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 8px;
}
.ilm-numpad__title {
  flex: 1 1 auto;
  text-align: center;
  font-size: 15px;
  font-weight: 700;
  color: #37474f;
}
.ilm-numpad__header-spacer {
  flex: 0 0 40px;
  width: 40px;
}
.ilm-numpad__back {
  flex: 0 0 40px;
}
.ilm-numpad__display {
  margin-bottom: 12px;
  padding: 12px 16px 14px;
  border-radius: 14px;
  border: 1px solid #d5dde8;
  background: linear-gradient(180deg, #f8f9fc 0%, #eef1f6 100%);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.85);
}
.ilm-numpad__display-label {
  margin-bottom: 4px;
  font-size: 12px;
  font-weight: 600;
  color: #8a96a8;
  text-align: right;
}
.ilm-numpad__display-value {
  min-height: 40px;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  font-size: 32px;
  font-weight: 800;
  color: #5b7c99;
  direction: ltr;
  unicode-bidi: plaintext;
  letter-spacing: 0.03em;
  line-height: 1.1;
}
.ilm-numpad__pad {
  display: grid;
  grid-template-columns: minmax(0, 3fr) minmax(0, 1fr);
  gap: 8px;
  margin-bottom: 10px;
}
.ilm-numpad__digits {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 8px;
}
.ilm-numpad__actions {
  display: grid;
  grid-template-rows: repeat(5, minmax(0, 1fr));
  gap: 8px;
}
.ilm-numpad__key {
  min-height: 48px;
  border: 0;
  border-radius: 12px;
  background: #fff;
  color: #263238;
  font-size: 22px;
  font-weight: 800;
  box-shadow: 0 1px 0 rgba(55, 71, 79, 0.08);
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}
.ilm-numpad__key:active {
  background: #eceff1;
}
.ilm-numpad__key--multi {
  font-size: 16px;
}
.ilm-numpad__key--action {
  background: #e8edf5;
  color: #5b7c99;
  font-size: 18px;
  border: 1px solid #d0d8e4;
}
.ilm-numpad__key--action:active {
  background: #d9e1ec;
}
.ilm-numpad__footer {
  margin-top: 2px;
}
.ilm-numpad__confirm {
  width: 100%;
  min-height: 48px;
  border: 1px solid #c5d0e0;
  border-radius: 12px;
  background: #e8edf5;
  color: #5b7c99;
  font-size: 15px;
  font-weight: 800;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}
.ilm-numpad__confirm:active {
  background: #d9e1ec;
}
</style>
